import {
    CollisionDetection,
    DndContext,
    DragEndEvent,
    DragOverEvent,
    DragOverlay,
    DragStartEvent,
    KeyboardSensor,
    MouseSensor,
    TouchSensor,
    UniqueIdentifier,
    pointerWithin,
    useDroppable,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    SortableContext,
    arrayMove,
    horizontalListSortingStrategy,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { keepPreviousData } from "@tanstack/react-query";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { trpc } from "~/api/trpc";
import Content from "~/components/layout/Content";
import { StoreSelector } from "~/pages/AppointmentsCalendar";
import { useAppointmentsCalendarStore } from "~/stores/appointments-calendar-store";
import { AppointmentLiveBoardGroup } from "~/types/server-types/AppointmentLiveBoardGroup";

import dayjs from "dayjs";
import { AlertTriangle, Cake, ChevronDown, ChevronUp, Clock, ClockIcon, Cog, Edit3, Euro, Eye, FileText, GiftIcon, HandCoins, HomeIcon, Hourglass, Loader2, MessageCircle, Package, Save, ScanFace, Stethoscope, StickyNoteIcon, TagIcon, TestTube, Trash2, UsersIcon, Wallet, XCircle } from "lucide-react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "~/components/ui/alert-dialog";
import { AutoComplete } from "~/components/ui/autocomplete";
import { Avatar, AvatarFallback } from "~/components/ui/avatar";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader } from "~/components/ui/card";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/components/ui/select";
import { Separator } from "~/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "~/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import LocalDateTime from "~/lib/localdatetime";
import { useCreateOrModifyAppointmentStore } from "~/stores/create-appointment-store";
import { useUserStore } from "~/stores/user-store";
import { AppointmentStatus } from "~/types/server-types/AppointmentStatus";
import { QuestionnaireResponseModal } from "./Customer";
import { SendAnamneseUrlDialog } from "~/components/customer/SendAnamneseUrlDialog";
import { useSidebar } from "~/components/ui/sidebar";
import BillingDraftCreatorDialog from "~/components/billing-draft-creator/BillingDraftCreatorDialog";
import BillingDraftDetailDialog from "~/components/billing-draft-creator/BillingDraftDetailDialog";
import { AddTreatmentDialog } from "~/components/medical-record";
import AppointmentChatDialog, { type AppointmentChatTarget } from "~/components/appointments/AppointmentChatDialog";

const isEventInsideOverlay = (element: EventTarget | null): boolean => {
    if (!(element instanceof HTMLElement)) {
        return false;
    }
    return !!element.closest('[role="dialog"], [role="alertdialog"], [data-radix-dialog-overlay], [data-radix-alert-dialog-overlay]');
};

class DialogAwareMouseSensor extends MouseSensor {
    static activators = [
        {
            eventName: 'onMouseDown' as const,
            handler: ({ nativeEvent: event }: React.MouseEvent) => {
                if (event.button !== 0) {
                    return false;
                }
                return !isEventInsideOverlay(event.target);
            },
        },
    ];
}

class DialogAwareTouchSensor extends TouchSensor {
    static activators = [
        {
            eventName: 'onTouchStart' as const,
            handler: ({ nativeEvent: event }: React.TouchEvent) => {
                return !isEventInsideOverlay(event.target);
            },
        },
    ];
}

class DialogAwareKeyboardSensor extends KeyboardSensor {
    static activators = [
        {
            eventName: 'onKeyDown' as const,
            handler: ({ nativeEvent: event }: React.KeyboardEvent) => {
                return !isEventInsideOverlay(event.target);
            },
        },
    ];
}

type BillingDraftDialogContextType = {
    openCreator: (customerId: number, appointmentId: string) => void;
    openDetail: (billingDraftId: string, appointmentId: string) => void;
};

const BillingDraftDialogContext = React.createContext<BillingDraftDialogContextType | null>(null);

const useBillingDraftDialog = () => {
    const ctx = useContext(BillingDraftDialogContext);
    if (!ctx) {
        throw new Error("useBillingDraftDialog must be used within BillingDraftDialogContext");
    }
    return ctx;
};

type DocDialogState = {
    mode: 'new' | 'edit';
    customerId: number;
    appointmentId: string;
    treatmentId?: string | null;
};

type DocDialogContextType = {
    openDocDialog: (params: DocDialogState) => void;
};

const DocDialogContext = React.createContext<DocDialogContextType | null>(null);

const useDocDialog = () => {
    const ctx = useContext(DocDialogContext);
    if (!ctx) {
        throw new Error("useDocDialog must be used within DocDialogContext");
    }
    return ctx;
};

type AppointmentChatContextType = {
    openChat: (target: AppointmentChatTarget) => void;
    unreadCounts: Record<string, number>;
    totalCounts: Record<string, number>;
};

const AppointmentChatContext = React.createContext<AppointmentChatContextType | null>(null);

const useAppointmentChat = () => {
    const ctx = useContext(AppointmentChatContext);
    if (!ctx) {
        throw new Error("useAppointmentChat must be used within AppointmentChatContext");
    }
    return ctx;
};

const OLD_PRICE_CUTOFF = new Date('2026-04-30T22:00:00Z'); // May 1st 2026 00:00 Berlin time

const isAppointmentOldPrice = (createdAt: Date) => {
    return createdAt < OLD_PRICE_CUTOFF;
};

const customCollisionDetection: CollisionDetection = (args) => {
    const { active, droppableContainers, droppableRects, pointerCoordinates } = args;

    if (!pointerCoordinates) {
        return pointerWithin(args);
    }

    // Get all potential collisions
    const intersections = [];

    for (const droppableContainer of droppableContainers) {
        const { id } = droppableContainer;
        const rect = droppableRects.get(id);

        if (!rect) continue;

        // Calculate distance from pointer to center of container
        const centerX = rect.left + rect.width / 2;
        const centerY = rect.top + rect.height / 2;
        const distance = Math.sqrt(
            Math.pow(pointerCoordinates.x - centerX, 2) +
            Math.pow(pointerCoordinates.y - centerY, 2)
        );

        // For collapsed containers, add a buffer zone to make them more stable
        const isCollapsed = rect.height < 100; // Assume containers under 100px are collapsed
        const buffer = isCollapsed ? 30 : 0; // 30px buffer for collapsed containers

        // Check if pointer is within the container bounds (with buffer for collapsed)
        const withinBounds =
            pointerCoordinates.x >= rect.left - buffer &&
            pointerCoordinates.x <= rect.right + buffer &&
            pointerCoordinates.y >= rect.top - buffer &&
            pointerCoordinates.y <= rect.bottom + buffer;

        if (withinBounds) {
            intersections.push({
                id,
                data: { droppableContainer, distance, isCollapsed }
            });
        }
    }

    if (intersections.length === 0) {
        return [];
    }

    // Sort by distance, but give preference to non-collapsed containers
    intersections.sort((a, b) => {
        const aCollapsed = a.data.isCollapsed;
        const bCollapsed = b.data.isCollapsed;

        // If one is collapsed and the other isn't, prefer the non-collapsed one
        if (aCollapsed && !bCollapsed) return 1;
        if (!aCollapsed && bCollapsed) return -1;

        // Otherwise, sort by distance
        return a.data.distance - b.data.distance;
    });

    return [{ id: intersections[0].id }];
};

// Radix tooltips ignore touch input, so tap toggling is handled manually for iPad use.
function TapTooltip({ trigger, children }: { trigger: React.ReactNode; children: React.ReactNode }) {
    const [open, setOpen] = useState(false);
    return (
        <Tooltip open={open} onOpenChange={setOpen}>
            <TooltipTrigger asChild>
                <span
                    className="inline-flex items-center"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                        // Prevents Radix's own trigger click handler from immediately closing the tooltip again.
                        e.preventDefault();
                        e.stopPropagation();
                        setOpen(o => !o);
                    }}
                >
                    {trigger}
                </span>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">{children}</TooltipContent>
        </Tooltip>
    );
}

type AppointmentData = {
    id: string;
    title: string;
    status: AppointmentStatus;
    type: 'NORMAL' | 'INFLUENCER' | 'MARKETING' | 'ACADEMY' | 'KULANZ';
    createdAt: Date;
    couponCodes: string;
    messageFromCustomer: string;
    appointmentNotes: string;
    liveBoardGroupStatus: AppointmentLiveBoardGroup;
    liveWaitingRoomId: string | null;
    liveBoardGroupEntryCreatedTimeUtc: Date | null;
    earliestLiveBoardGroupEntryCreatedTimeUtc: Date | null;
    liveRoomId: number | null;
    startTime: LocalDateTime;
    endTime: LocalDateTime;
    customer: {
        id: number;
        praxismanagerId: number | null;
        firstName: string;
        lastName: string;
        email: string | null;
        phone: string | null;
        birthday: string | null;
        note: string | null;
        latestQuestionnaireResponse: {
            id: number;
            createdAt: Date;
        } | null;
    };
    variants: { id: string; name: string; }[];
    practitioners: { id: number; name: string; }[];
    rooms: { id: number; name: string; }[];
    equipment: { id: number; name: string; }[];
};

type ItemType = {
    id: UniqueIdentifier;
    title: string;
    color?: string;
    parent?: UniqueIdentifier;
    container?: boolean;
    isCollapsible?: boolean;
    defaultCollapsed?: boolean;
    childrenLayout?: 'horizontal' | 'vertical';
    appointmentData?: AppointmentData;
};

// Helper to format price
const formatCentsToEuros = (cents: number): string => {
    return (cents / 100).toFixed(2);
};
const parseEurosToCents = (eurosStr: string): number | null => {
    const num = parseFloat(eurosStr.replace(',', '.')); // Allow comma as decimal separator
    if (isNaN(num)) return null;
    return Math.round(num * 100);
};
type LineItem = {
    treatmentDocumentationId?: string;
    treatmentVariantId?: string | null;
    treatmentName: string;
    priceCents: number;
    notes?: string;
};
type LineItemFormState = {
    treatmentName: string;
    priceEuros: string;
    notes?: string;
};
const initialFormState: LineItemFormState = {
    treatmentName: '',
    priceEuros: '',
    notes: '',
};

type DiscountType = 'percentage' | 'fixed';

type SendPaymentLinkButtonProps = {
    appointmentId: string;
    customerId: number;
    customerName: string;
}

const SendPaymentLinkButton = ({ appointmentId, customerId, customerName }: SendPaymentLinkButtonProps) => {
    const { selectedStore } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            selectedStore: state.selectedStore,
        }))
    );
    const trpcUtils = trpc.useUtils();

    const [treatmentSearchQuery, setTreatmentSearchQuery] = useState<string>("");
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    const { data: treatmentVariantsData, isLoading: isLoadingVariants } = trpc.treatmentVariants.getAll.useQuery({
        storeId: selectedStore!.id,
        filters: {
            name: treatmentSearchQuery,
        },
        pageSize: 50,
    }, {
        enabled: !!selectedStore?.id
    });

    const { data: appointmentData } = trpc.appointments.getAppointmentForPaymentLink.useQuery({
        appointmentId: appointmentId ?? '',
    }, {
        staleTime: 15 * 1000,
        enabled: !!appointmentId && isDialogOpen,
    });
    const selectedVariants = useMemo(() => {
        return appointmentData?.originalTreatmentVariants?.map((variant) => ({
            id: variant.treatmentVariant.id,
            name: variant.treatmentVariant.name,
            priceCents: variant.treatmentVariant.priceCents,
        })) ?? [];
    }, [appointmentData]);

    const _treatmentVariantsData = useMemo(() => {
        return treatmentVariantsData?.data ?? []
    }, [treatmentVariantsData?.data]);
    const setAppointmentTreatmentDocumentationMutation = trpc.treatmentDocumentation.setAppointmentTreatmentDocumentation.useMutation({
        onSuccess: () => {
            toast.success("Successfully saved documented treatments.");
            setIsDialogOpen(false);
            trpcUtils.treatmentDocumentation.getByAppointment.invalidate();
        },
        onError: (error) => {
            toast.error(`Failed to save documented treatments: ${error.message}`);
        }
    });
    const sendPaymentLinkMutation = trpc.customers.sendPaymentLink.useMutation({
        onSuccess: () => {
            toast.success("Payment link sent successfully!"); // Added success toast
        },
        onError: (error) => {
            toast.error(`Failed to send payment link: ${error.message}`);
        }
    });
    const documentationQuery = trpc.treatmentDocumentation.getByAppointment.useQuery({
        appointmentId: appointmentId ?? '',
    }, {
        staleTime: 5 * 60 * 1000,
        enabled: !!appointmentId,
    })
    const [lineItems, setLineItems] = useState<LineItem[]>([]);
    const [newItemForm, setNewItemForm] = useState<LineItemFormState>(initialFormState);
    const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
    const [editItemForm, setEditItemForm] = useState<LineItemFormState>(initialFormState);

    // New state for discount
    const [discountValue, setDiscountValue] = useState<string>('');
    const [discountType, setDiscountType] = useState<DiscountType>('fixed');

    const handleSelectVariant = useCallback((variant: typeof _treatmentVariantsData[0]) => {
        setLineItems((prev) => [...prev, {
            treatmentVariantId: variant.id,
            treatmentName: variant.name,
            priceCents: variant.priceCents,
        }])
    }, []);
    const treatmentOptions = useMemo(() => {
        return _treatmentVariantsData
            .filter((variant) => {
                const searchLower = treatmentSearchQuery.toLowerCase();
                return (
                    variant.name.toLowerCase().includes(searchLower) ||
                    (variant.treatment.name && variant.treatment.name.toLowerCase().includes(searchLower))
                );
            })
            .filter((variant) => {
                return variant.steps.length > 0;
            })
            .sort((a, b) => {
                const categoryA = a.treatment.category?.name || '';
                const categoryB = b.treatment.category?.name || '';
                if (categoryA !== categoryB) {
                    return categoryA.localeCompare(categoryB);
                }
                const treatmentNameA = a.treatment.name || '';
                const treatmentNameB = b.treatment.name || '';
                if (treatmentNameA !== treatmentNameB) {
                    return treatmentNameA.localeCompare(treatmentNameB);
                }
                return a.name.localeCompare(b.name);
            })
            .map((variant) => ({
                label: `${variant.name} - ${variant.treatment.name || 'Unknown Treatment'}`,
                value: variant.id,
                renderItem: (
                    <div className='w-full flex flex-col'>
                        <span className='text-xs text-muted-foreground'>{variant.treatment.name || 'Unknown Treatment'}</span>
                        <div className='flex flex-row items-center gap-2'>
                            <span>{variant.name}</span>
                        </div>
                    </div>
                ),
            }));
    }, [_treatmentVariantsData, treatmentSearchQuery]);
    useEffect(() => {
        if (documentationQuery && documentationQuery.data && documentationQuery.data.length > 0) {
            setLineItems(documentationQuery.data.map((documentation) => ({
                treatmentDocumentationId: documentation.id,
                treatmentVariantId: documentation.treatmentVariantId,
                treatmentName: documentation.treatmentName,
                priceCents: documentation.priceCents,
                notes: documentation.notes,
            })))
        }
    }, [documentationQuery.data]);
    // Effect to initialize lineItems from selectedVariants
    useEffect(() => {
        if (!isDialogOpen) return;
        const newLineItems = selectedVariants.map((variant) => ({
            treatmentVariantId: variant.id,
            treatmentName: variant.name,
            priceCents: variant.priceCents,
        }));

        if (JSON.stringify(lineItems) !== JSON.stringify(newLineItems) && editingItemIndex === null) {
            if (lineItems.length === 0 || !lineItems.some((li, idx) => JSON.stringify(li) !== JSON.stringify(newLineItems[idx]))) {
                setLineItems(newLineItems);
            }
        } else if (selectedVariants.length === 0 && lineItems.length > 0 && editingItemIndex === null) {
            setLineItems([]);
        }
    }, [selectedVariants, isDialogOpen]);
    // Reset forms when dialog closes
    useEffect(() => {
        if (!isDialogOpen) {
            setNewItemForm(initialFormState);
            setEditItemForm(initialFormState);
            setEditingItemIndex(null);
            setDiscountValue(''); // Reset discount
            setDiscountType('fixed'); // Reset discount type
        }
    }, [isDialogOpen]);

    // Calculate total price and discounted price
    const originalTotalPriceCents = useMemo(() => {
        return lineItems.reduce((sum, item) => sum + item.priceCents, 0);
    }, [lineItems]);

    const discountedTotalPriceCents = useMemo(() => {
        let total = originalTotalPriceCents;
        const parsedDiscount = parseFloat(discountValue.replace(',', '.'));

        if (isNaN(parsedDiscount) || parsedDiscount <= 0) {
            return total;
        }

        if (discountType === 'percentage') {
            total = total * (1 - parsedDiscount / 100);
        } else { // fixed
            total = total - (parsedDiscount * 100);
        }
        return Math.max(0, Math.round(total)); // Ensure total doesn't go below zero
    }, [originalTotalPriceCents, discountValue, discountType]);

    const discountAmountCents = originalTotalPriceCents - discountedTotalPriceCents;

    const handleSubmitDocumentation = async () => {
        if (editingItemIndex !== null) {
            toast.error("Please save or cancel your current edit before submitting.");
            return;
        }
        if (!appointmentId || !selectedStore || lineItems.length === 0) {
            toast.error('Please make sure to select an existing appointment, customer, location, and add line items before submitting.');
            return;
        }

        const itemsToSend = lineItems.map((item) => ({
            name: item.treatmentName,
            priceCents: item.priceCents,
            quantity: 1,
        }));

        // Wichtig: Wir fügen den Rabatt NICHT mehr als negatives Line-Item hier hinzu.
        // Stattdessen senden wir den finalen Gesamtpreis separat.

        // Zuerst die Behandlungsdokumentation speichern
        setAppointmentTreatmentDocumentationMutation.mutate({
            appointmentId: appointmentId,
            documentationList: lineItems, // Hier werden nur die originalen Behandlungen dokumentiert
        });

        // Dann den Zahlungslink senden
        sendPaymentLinkMutation.mutate({
            appointmentId: appointmentId,
            customerId: customerId,
            storeId: selectedStore.id,
            lineItems: itemsToSend, // Nur die positiven Line-Items der Behandlungen
            // NEU: Rabattwerte und den rabattierten Gesamtbetrag direkt an die Mutation übergeben
            discountValue: discountValue, // Keep as string as per your state
            discountType: discountType,
            // finalTotalPriceCents: discountedTotalPriceCents, // The discounted final price in cents
        });
    };
    const handleAddNewItem = () => {
        if (editingItemIndex !== null) {
            toast.error("Please save or cancel your current edit before adding a new item.");
            return;
        }
        const priceCents = parseEurosToCents(newItemForm.priceEuros);

        if (!newItemForm.treatmentName.trim()) {
            toast.error("Treatment name cannot be empty.");
            return;
        }
        if (priceCents === null || priceCents < 0) {
            toast.error("Invalid price. Please enter a valid number (e.g., 10.50).");
            return;
        }
        setLineItems([...lineItems, {
            treatmentName: newItemForm.treatmentName.trim(),
            priceCents,
            notes: newItemForm.notes?.trim() || undefined
        }]);
        setNewItemForm(initialFormState);
    };
    const handleDeleteItem = (indexToDelete: number) => {
        setLineItems(prevLineItems => prevLineItems.filter((_, i) => i !== indexToDelete));
        if (editingItemIndex === indexToDelete) {
            setEditingItemIndex(null);
            setEditItemForm(initialFormState);
        } else if (editingItemIndex !== null && indexToDelete < editingItemIndex) {
            setEditingItemIndex(prevIndex => prevIndex! - 1);
        }
    };
    const handleStartEdit = (index: number) => {
        if (editingItemIndex !== null && editingItemIndex !== index) {
            console.warn("Switched edit focus, previous unsaved changes lost.");
        }
        const itemToEdit = lineItems[index];
        setEditingItemIndex(index);
        setEditItemForm({
            treatmentName: itemToEdit.treatmentName,
            priceEuros: formatCentsToEuros(itemToEdit.priceCents),
            notes: itemToEdit.notes || '',
        });
    };
    const handleCancelEdit = () => {
        setEditingItemIndex(null);
        setEditItemForm(initialFormState);
    };
    const handleSaveEdit = () => {
        if (editingItemIndex === null) return;
        const priceCents = parseEurosToCents(editItemForm.priceEuros);

        if (!editItemForm.treatmentName.trim()) {
            toast.error("Treatment name cannot be empty.");
            return;
        }
        if (priceCents === null || priceCents < 0) {
            toast.error("Invalid price. Please enter a valid number (e.g., 10.50).");
            return;
        }
        const updatedLineItems = lineItems.map((item, index) =>
            index === editingItemIndex
                ? {
                    ...item,
                    treatmentName: editItemForm.treatmentName.trim(),
                    priceCents,
                    notes: editItemForm.notes?.trim() || undefined
                }
                : item
        );
        setLineItems(updatedLineItems);
        setEditingItemIndex(null);
        setEditItemForm(initialFormState);
    };
    if (!selectedStore) {
        return null;
    }
    return (
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
                <Button
                    variant='outline'
                    size="sm"
                    className="h-7 px-2 text-xs flex-1"
                >
                    <Stethoscope className="h-4 w-4 mr-2" />
                    Zahlung erstellen
                </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>Zahlung erstellen</DialogTitle>
                    <DialogDescription>
                        Bestätigen der Behandlungen, die durchgeführt wurden bei: <b>{customerName}</b>.
                    </DialogDescription>
                </DialogHeader>
                <div className="flex flex-col flex-grow gap-8">
                    {/* Form for Adding New Line Item (moved to the top) */}
                    <div className='pb-4'>
                        {editingItemIndex === null && (
                            <div className='flex flex-col gap-8 p-4 rounded-xl border border-gray-200'>
                                <div className='flex flex-col gap-4'>
                                    <span className='text-lg'>Behandlung auswählen</span>
                                    <AutoComplete
                                        selectedValue={''}
                                        onSelectedValueChange={(value) => {
                                            const selectedVariant = _treatmentVariantsData.find((v) => v.id === value);
                                            if (selectedVariant) {
                                                handleSelectVariant(selectedVariant);
                                            }
                                        }}
                                        searchValue={treatmentSearchQuery}
                                        onSearchValueChange={setTreatmentSearchQuery}
                                        items={treatmentOptions}
                                        placeholder='Behandlung auswählen...'
                                        emptyMessage='Keine Behandlung gefunden'
                                        isLoading={isLoadingVariants}
                                        resetSearchOnSelectItem
                                    />
                                </div>
                                {/* <div className='flex flex-col gap-4'>
                                    <span className='text-lg'>Oder manuell hinzufügen</span>
                                    <div className='flex flex-col gap-2'>
                                        <Input
                                            value={newItemForm.treatmentName}
                                            onChange={(e) => setNewItemForm({ ...newItemForm, treatmentName: e.target.value })}
                                            placeholder="Behandlungsname"
                                        />
                                        <Input
                                            value={newItemForm.priceEuros}
                                            onChange={(e) => setNewItemForm({ ...newItemForm, priceEuros: e.target.value })}
                                            placeholder="Preis (z.B. 10.50)"
                                        />
                                        <Input
                                            value={newItemForm.notes || ''}
                                            onChange={(e) => setNewItemForm({ ...newItemForm, notes: e.target.value })}
                                            placeholder="Notizen (optional)"
                                        />
                                        <Button onClick={handleAddNewItem} className="mt-2">
                                            Hinzufügen
                                        </Button>
                                    </div>
                                </div> */}
                            </div>
                        )}
                        {editingItemIndex !== null && (
                            <p className="text-sm text-muted-foreground text-center py-2">
                                Finish editing the current item to add a new one.
                            </p>
                        )}
                    </div>

                    <div className='border-b max-h-[300px] overflow-y-auto'>
                        <div className="text-sm font-medium mb-2">Behandlungen:</div>
                        {lineItems.length > 0 ? (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        {<TableHead className="w-[100px] text-right">Preis</TableHead>}
                                        <TableHead className="w-[130px] text-right">Aktionen</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {lineItems.map((item, index) => (
                                        <TableRow key={index} className={editingItemIndex === index ? "bg-muted/50" : ""}>
                                            {editingItemIndex === index ? (
                                                <>
                                                    <TableCell>
                                                        <div className="space-y-2">
                                                            <Input
                                                                value={editItemForm.treatmentName}
                                                                onChange={(e) => setEditItemForm({ ...editItemForm, treatmentName: e.target.value })}
                                                                placeholder="Treatment name"
                                                                className="h-8"
                                                            />
                                                            <Input
                                                                value={editItemForm.notes || ''}
                                                                onChange={(e) => setEditItemForm({ ...editItemForm, notes: e.target.value })}
                                                                placeholder="Notizen (optional)"
                                                                className="h-8"
                                                            />
                                                        </div>
                                                    </TableCell>
                                                    {<TableCell>
                                                        <Input
                                                            value={editItemForm.priceEuros}
                                                            onChange={(e) => setEditItemForm({ ...editItemForm, priceEuros: e.target.value })}
                                                            placeholder="0.00"
                                                            className="h-8 text-right"
                                                        />
                                                    </TableCell>}
                                                    <TableCell className="text-right space-x-1">
                                                        <Button variant="ghost" size="icon" onClick={handleSaveEdit} title="Save changes">
                                                            <Save className="h-4 w-4 text-green-600" />
                                                        </Button>
                                                        <Button variant="ghost" size="icon" onClick={handleCancelEdit} title="Cancel edit">
                                                            <XCircle className="h-4 w-4 text-red-600" />
                                                        </Button>
                                                    </TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell>
                                                        <div>
                                                            {item.treatmentName}
                                                            {item.notes && (
                                                                <div className="text-xs text-muted-foreground mt-1">
                                                                    {item.notes}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    {<TableCell className="text-right">€{formatCentsToEuros(item.priceCents)}</TableCell>}
                                                    <TableCell className="text-right space-x-1">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleStartEdit(index)}
                                                            disabled={editingItemIndex !== null && editingItemIndex !== index}
                                                            title="Bearbeiten"
                                                        >
                                                            <Edit3 className="h-4 w-4" />
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            onClick={() => handleDeleteItem(index)}
                                                            disabled={editingItemIndex === index}
                                                            title="Löschen"
                                                            className="text-red-500 hover:text-red-600 disabled:text-muted-foreground"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        ) : (
                            <p className="text-sm text-muted-foreground text-center py-4">Keine Behandlung ausgewählt.</p>
                        )}
                    </div>

                    {/* Discount Input */}
                    <div className="space-y-2">
                        <Label htmlFor="discount">Rabatt hinzufügen</Label>
                        <div className="flex items-center gap-2">
                            <Input
                                id="discount"
                                value={discountValue}
                                onChange={(e) => setDiscountValue(e.target.value)}
                                placeholder={discountType === 'percentage' ? "z.B. 10" : "z.B. 5.00"}
                                className="w-1/2"
                            />
                            <Select value={discountType} onValueChange={(value: DiscountType) => setDiscountType(value)}>
                                <SelectTrigger className="w-1/2">
                                    <SelectValue placeholder="Rabatt-Typ" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="fixed">Fester Betrag (€)</SelectItem>
                                    <SelectItem value="percentage">Prozentsatz (%)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Total Price Display */}
                    <div className="flex flex-col gap-1 text-right mt-4">
                        <div className="flex justify-between items-center">
                            <span className="text-sm font-medium">Original Gesamt:</span>
                            <span className="text-sm font-medium">€{formatCentsToEuros(originalTotalPriceCents)}</span>
                        </div>
                        {discountAmountCents > 0 && (
                            <div className="flex justify-between items-center text-red-500">
                                <span className="text-sm font-medium">Rabatt:</span>
                                <span className="text-sm font-medium">-€{formatCentsToEuros(discountAmountCents)}</span>
                            </div>
                        )}
                        <Separator className="my-1" />
                        <div className="flex justify-between items-center text-lg font-bold">
                            <span>Gesamtbetrag:</span>
                            <span>€{formatCentsToEuros(discountedTotalPriceCents)}</span>
                        </div>
                    </div>
                </div>
                <DialogFooter className="mt-auto pt-4">
                    <DialogClose asChild>
                        <Button variant='outline'>Abbrechen</Button>
                    </DialogClose>
                    <Button
                        onClick={handleSubmitDocumentation}
                        disabled={
                            !selectedStore ||
                            lineItems.length === 0 ||
                            sendPaymentLinkMutation.isPending ||
                            setAppointmentTreatmentDocumentationMutation.isPending ||
                            editingItemIndex !== null
                        }
                    >
                        {(sendPaymentLinkMutation.isPending || setAppointmentTreatmentDocumentationMutation.isPending) && <Loader2 className='animate-spin mr-2 h-4 w-4' />}
                        Zahlungslink senden
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

type QuestionnaireResponseModalBadgeProps = {
    questionnaireResponseId: number;
    createdAt: Date;
}
const QuestionnaireResponseModalBadge = ({ questionnaireResponseId, createdAt }: QuestionnaireResponseModalBadgeProps) => {
    const [questionnaireResponseModalOpen, setQuestionnaireResponseModalOpen] = useState(false);

    return (
        <div className="flex items-center">
            <QuestionnaireResponseModal
                questionnaireResponseId={questionnaireResponseId}
                open={questionnaireResponseModalOpen}
                onClose={() => setQuestionnaireResponseModalOpen(false)}
            />
            <FileText className="h-3 w-3 mr-1.5 text-muted-foreground flex-shrink-0" />
            <Badge variant='outline' className='w-min py-0 bg-green-100 text-green-900 underline cursor-pointer whitespace-pre-wrap' onClick={() => setQuestionnaireResponseModalOpen(true)}>
                Anamnese abgeschlossen
            </Badge>
        </div>
    )
}


type ItemProps = {
    id: UniqueIdentifier;
    title: string;
    appointmentData?: AppointmentData;
}

const Item = React.memo(({ id, title, appointmentData }: ItemProps) => {
    const { timezone, currentPractitionerId, practitionerCategoryId } = useUserStore(
        useShallow((state) => ({
            timezone: state.user?.timezone || 'Europe/Berlin',
            currentPractitionerId: state.user?.practitionerId ?? null,
            practitionerCategoryId: state.user?.practitionerCategoryId ?? null,
        }))
    );
    const canCreateDocumentation = practitionerCategoryId === 1 || practitionerCategoryId === 2;

    const [, forceUpdate] = useState({});

    useEffect(() => {
        // Force re-render every 5 seconds to update time-dependent calculations
        const interval = setInterval(() => {
            forceUpdate({});
        }, 5000);

        return () => clearInterval(interval);
    }, []);

    const { setMode, setAppointmentId, setNotifyCustomer } = useCreateOrModifyAppointmentStore(
        useShallow((state) => ({
            setMode: state.setMode,
            setAppointmentId: state.setAppointmentId,
            setNotifyCustomer: state.setNotifyCustomer,
        }))
    );
    const {
        setCreateOrModifyAppointmentDrawerOpen,
    } = useAppointmentsCalendarStore(
        useShallow((state) => ({
            setCreateOrModifyAppointmentDrawerOpen: state.setCreateOrModifyAppointmentDrawerOpen,
        }))
    );

    const trpcUtils = trpc.useUtils();

    const billingDraftDialog = useBillingDraftDialog();
    const appointmentChat = useAppointmentChat();
    const chatUnreadCount = appointmentData?.id ? (appointmentChat.unreadCounts[appointmentData.id] ?? 0) : 0;
    const chatTotalCount = appointmentData?.id ? (appointmentChat.totalCounts[appointmentData.id] ?? 0) : 0;

    const { data: existingBillingDrafts } = trpc.billingDrafts.getByAppointment.useQuery(
        { appointmentId: appointmentData?.id ?? '' },
        { enabled: !!appointmentData?.id && appointmentData?.liveBoardGroupStatus === AppointmentLiveBoardGroup.FINALIZING }
    );

    const latestActiveBillingDraft = existingBillingDrafts?.find((draft) => draft.status !== 'CANCELLED') ?? null;
    const hasBillingDraft = !!latestActiveBillingDraft;

    const nonCancelledDrafts = existingBillingDrafts?.filter((draft) => draft.status !== 'CANCELLED') ?? [];
    const isPaid = nonCancelledDrafts.length > 0 && nonCancelledDrafts.every((draft) => draft.status === 'PAID');
    const hasPendingDraft = nonCancelledDrafts.some((draft) => draft.status === 'PENDING_PAYMENT');

    const markNoShowMutation = trpc.appointments.markNoshow.useMutation({
        onSuccess: (_data, variables) => {
            toast.success("Appointment was successfully marked as a NO-SHOW.")
            trpcUtils.appointments.getAppointmentsForBoard.invalidate();
        },
        onError: (error, variables) => {
            toast.error("Failed to mark appointment as NO-SHOW.", {
                description: error.message
            })
        }
    });
    const markCompletedMutation = trpc.appointments.markCompleted.useMutation({
        onSuccess: (_data, variables) => {
            toast.success("Appointment was successfully marked as a COMPLETED.")
            trpcUtils.appointments.getAppointmentsForBoard.invalidate();
        },
        onError: (error, variables) => {
            toast.error("Failed to mark appointment as COMPLETED.", {
                description: error.message
            })
        }
    });

    const skinReportsQuery = trpc.customers.getCustomerSimpleSkinReports.useQuery({
        id: appointmentData?.customer?.id || 0,
    }, {
        staleTime: 15 * 1000,
        refetchInterval: 15 * 1000,
        enabled: appointmentData?.customer?.id !== undefined,
    });

    const customerBalanceQuery = trpc.customerMoney.getCustomerBalance.useQuery(
        { customerId: appointmentData?.customer?.id ?? 0 },
        {
            staleTime: 15 * 1000,
            refetchInterval: 15 * 1000,
            enabled: appointmentData?.customer?.id !== undefined,
        }
    );
    const customerBalanceEur = customerBalanceQuery.data?.balanceEur ?? 0;
    const hasNegativeBalance = customerBalanceEur < 0;

    const customerBundleVouchersQuery = trpc.customerMoney.getCustomerBundleVouchers.useQuery(
        { customerId: appointmentData?.customer?.id ?? 0 },
        {
            staleTime: 15 * 1000,
            refetchInterval: 15 * 1000,
            enabled: appointmentData?.customer?.id !== undefined,
        }
    );
    const activeBundleVouchers = (customerBundleVouchersQuery.data ?? []).filter(v => v.remainingUnits > 0);
    const hasActiveBundle = activeBundleVouchers.length > 0;

    const isTreatmentColumn = appointmentData?.liveBoardGroupStatus === AppointmentLiveBoardGroup.TREATMENT;
    const docDialog = useDocDialog();
    const latestTreatmentQuery = trpc.medicalRecord.getLatestTreatmentByAppointment.useQuery(
        { appointmentId: appointmentData?.id ?? '' },
        { enabled: !!appointmentData?.id && isTreatmentColumn }
    );
    const latestTreatment = latestTreatmentQuery.data ?? null;

    const showDocStatusBadge = appointmentData?.liveBoardGroupStatus === AppointmentLiveBoardGroup.PREPARATION
        || appointmentData?.liveBoardGroupStatus === AppointmentLiveBoardGroup.TREATMENT
        || appointmentData?.liveBoardGroupStatus === AppointmentLiveBoardGroup.FINALIZING;

    const docStatusQuery = trpc.medicalRecord.getDocumentationStatusForAppointment.useQuery(
        { appointmentId: appointmentData?.id ?? '' },
        { enabled: !!appointmentData?.id && showDocStatusBadge }
    );

    const { docTreatments, documentedNames, pendingPractitioners } = useMemo(() => {
        const docTreatments = docStatusQuery.data?.treatments ?? [];
        const documentedPractitionerIds = new Set(docTreatments.map(t => t.administeredByPractitioner.id));
        const documentedNames = docTreatments.map(t => `${t.administeredByPractitioner.firstName} ${t.administeredByPractitioner.lastName}`);
        const appointmentPractitioners = appointmentData?.practitioners ?? [];
        const pendingPractitioners = appointmentPractitioners.filter(p => !documentedPractitionerIds.has(p.id));
        return { docTreatments, documentedNames, pendingPractitioners };
    }, [docStatusQuery.data, appointmentData?.practitioners]);

    const hasMissingDocumentation = docStatusQuery.isSuccess && pendingPractitioners.length > 0;
    const pendingPractitionerNames = Array.from(new Set(pendingPractitioners.map(p => p.name)));
    const [missingDocWarningOpen, setMissingDocWarningOpen] = useState(false);

    const hasCompletedSkinReport = useMemo(() => {
        if (!skinReportsQuery.data || skinReportsQuery.data.length === 0) {
            return false;
        }
        const skinReports = skinReportsQuery.data;
        return skinReports.some((skinReport) => {
            const now = dayjs(new Date()).add(10, 'minutes'); // Just in case it doesn't update for a while.
            const tenHoursAgo = dayjs(new Date()).subtract(10, 'hours');
            const createdAt = dayjs(new Date(skinReport.created_at)).tz(timezone);
            return createdAt.isBetween(now, tenHoursAgo);
        })
    }, [skinReportsQuery.data, timezone]);

    const handleMarkNoshow = () => {
        if (!appointmentData?.id) {
            return;
        }
        const appointmentId = appointmentData.id;

        markNoShowMutation.mutate({
            appointmentId,
        });
    }

    const handleMarkCompleted = () => {
        if (!appointmentData?.id) {
            return;
        }
        const appointmentId = appointmentData.id;

        markCompletedMutation.mutate({
            appointmentId,
        });
    }

    if (appointmentData) {
        const { liveBoardGroupStatus, liveBoardGroupEntryCreatedTimeUtc, earliestLiveBoardGroupEntryCreatedTimeUtc, couponCodes, status, customer, startTime, endTime, variants, practitioners, rooms, equipment } = appointmentData;
        const customerInitials = `${customer.firstName?.charAt(0) ?? ''}${customer.lastName?.charAt(0) ?? ''}`.toUpperCase();

        const DAYS_SINCE_LATEST_QUESTIONNAIRE_RESPONSE_THRESHOLD = 10; // 10 days since last anamnese questionnaire response to be considered as completed
        const latestQuestionnaireResponse = customer.latestQuestionnaireResponse;
        const daysSinceLatestQuestionnaireResponse = latestQuestionnaireResponse ? dayjs().diff(dayjs(latestQuestionnaireResponse.createdAt), 'day') : null;
        const hasCompletedAnamneseQuestionnaire = daysSinceLatestQuestionnaireResponse !== null && daysSinceLatestQuestionnaireResponse <= DAYS_SINCE_LATEST_QUESTIONNAIRE_RESPONSE_THRESHOLD;

        // Convert UTC to the user's timezone
        const appointmentStart = startTime;
        const appointmentEnd = endTime;

        const liveBoardGroupEntryCreatedTime = !!liveBoardGroupEntryCreatedTimeUtc ? dayjs(liveBoardGroupEntryCreatedTimeUtc).tz(timezone) : null;
        const earliestLiveBoardGroupEntryCreatedTime = !!earliestLiveBoardGroupEntryCreatedTimeUtc ? dayjs(earliestLiveBoardGroupEntryCreatedTimeUtc).tz(timezone) : null;
        let _timeWaited: string | null = null;
        let _timeWaitedSeconds: number | null = null;
        let _totalTimeInClinic: string | null = null;
        if (!!liveBoardGroupEntryCreatedTime) {
            const now = dayjs().tz(timezone);
            const diffInSeconds = now.diff(liveBoardGroupEntryCreatedTime, 'second');
            _timeWaitedSeconds = diffInSeconds;

            if (diffInSeconds < 60) {
                _timeWaited = `${diffInSeconds} Sekunde${diffInSeconds !== 1 ? 'n' : ''}`;
            } else if (diffInSeconds < 3600) {
                const minutes = Math.floor(diffInSeconds / 60);
                _timeWaited = `${minutes} Minute${minutes !== 1 ? 'n' : ''}`;
            } else if (diffInSeconds < 86400) {
                const hours = Math.floor(diffInSeconds / 3600);
                _timeWaited = `${hours} Stunde${hours !== 1 ? 'n' : ''}`;
            } else {
                const days = Math.floor(diffInSeconds / 86400);
                _timeWaited = `${days} Tag${days !== 1 ? 'en' : ''}`;
            }
        }
        if (!!earliestLiveBoardGroupEntryCreatedTime) {
            const now = dayjs().tz(timezone);
            const diffInSeconds = now.diff(earliestLiveBoardGroupEntryCreatedTime, 'second');

            if (diffInSeconds < 60) {
                _totalTimeInClinic = `${diffInSeconds} Sekunde${diffInSeconds !== 1 ? 'n' : ''}`;
            } else if (diffInSeconds < 3600) {
                const minutes = Math.floor(diffInSeconds / 60);
                _totalTimeInClinic = `${minutes} Minute${minutes !== 1 ? 'n' : ''}`;
            } else if (diffInSeconds < 86400) {
                const hours = Math.floor(diffInSeconds / 3600);
                _totalTimeInClinic = `${hours} Stunde${hours !== 1 ? 'n' : ''}`;
            } else {
                const days = Math.floor(diffInSeconds / 86400);
                _totalTimeInClinic = `${days} Tag${days !== 1 ? 'en' : ''}`;
            }
        }

        const timeWaited = _timeWaited;
        const timeWaitedSeconds = _timeWaitedSeconds;
        const totalTimeInClinic = _totalTimeInClinic;
        const hasWaitedTooLong =
            timeWaitedSeconds !== null && timeWaitedSeconds > 5 * 60 && // Waited 5 minutes or more
            (liveBoardGroupStatus === AppointmentLiveBoardGroup.ARRIVED ||
                liveBoardGroupStatus === AppointmentLiveBoardGroup.SKINSCANNER ||
                (status !== AppointmentStatus.COMPLETED && liveBoardGroupStatus === AppointmentLiveBoardGroup.FINALIZING));

        const hasntYetArrived = liveBoardGroupStatus === AppointmentLiveBoardGroup.INITIAL && appointmentStart.toUTC().getTime() - dayjs(new Date()).tz(timezone).toDate().getTime() < 0;

        const customerAge = !customer ? null : customer.birthday
            ? `${Math.floor(
                (Date.now() - new Date(customer.birthday).getTime()) / (1000 * 60 * 60 * 24 * 365.25)
            )}`
            : null;

        const isUnder18 = customerAge !== null && parseInt(customerAge) < 18;

        const birthdayInfo = useMemo(() => {
            if (!customer?.birthday) return null;
            const birthday = new Date(customer.birthday);
            const today = new Date();
            const thisYearBirthday = new Date(today.getFullYear(), birthday.getMonth(), birthday.getDate());
            const diffTime = today.getTime() - thisYearBirthday.getTime();
            const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays >= 0 && diffDays <= 7) {
                return { daysAgo: diffDays };
            }
            return null;
        }, [customer?.birthday]);

        const hasAnyNote =
            (customer.note && customer.note.trim().length > 0) ||
            (appointmentData.messageFromCustomer && appointmentData.messageFromCustomer.trim().length > 0) ||
            (appointmentData.appointmentNotes && appointmentData.appointmentNotes.trim().length > 0);

        const checkShouldHighlight = (variants: AppointmentData['variants']): boolean => {
            const highlightedVariantIds = [
                '1868ec51-b508-4316-8411-0f940dffce9b',
                '11d6e6f4-703b-49ff-aa1e-431cc19a4b45',
                '218fccbc-06ae-48f9-b02d-c55bfc3fbe20',
                '9bad9196-28ee-4ffe-9c33-8cf46fa47370',
                '330aebb0-8998-401c-9048-866be79a59d6',
                'a87d4d65-b550-41e7-8b60-916b1353b936',
                '5b0db137-2152-488b-b6ed-e7f2349c5cff',
                '9c601f19-11d3-4526-8d4e-6b32a7706c1f',
                '45fcd878-3fc1-498c-af52-35d875442e7f',
                '66268647-7852-4a05-855b-fb09eea03fee',
                '53cc2058-874a-4408-9182-afac285469a7',
                'a1baa2a0-6c02-46c3-83d3-6fcc81eeeb24',
                '218e0cdc-60f7-4c7d-826b-60a6c1483631',
                '9b4ccdb1-a46d-41f6-ac60-691e1f6202a3',
                'd1c6e93d-62f1-4308-8fa5-9317d5ec58b3',
                'cfb89f94-77b1-47e1-94e3-8be4e29ebf4e',
                '2e38c1fc-4bf7-4e89-a142-e75dd5672a92',
                'a3a64a75-6d03-418a-b4b0-73f21990898d'
            ];
            return variants.some(variant => highlightedVariantIds.includes(variant.id));
        };
        const shouldHighlight = checkShouldHighlight(variants);
        const isInfluencerAppointment = appointmentData.type === 'INFLUENCER';

        return (
            <>
                <Card
                    className={`w-full max-w-[90%] m-1 mx-auto py-4 shadow-md cursor-default gap-3 border ${hasntYetArrived || hasWaitedTooLong
                        ? 'border-red-500 border-2 bg-red-50'
                        : shouldHighlight
                            ? 'bg-[rgba(168,85,247,0.05)] border-purple-500 border-2'
                            : isInfluencerAppointment
                                ? 'bg-[rgba(250,204,21,0.08)] border-yellow-400 border-2'
                                : ''
                        }`}
                    style={{
                        WebkitTouchCallout: 'none',
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                        WebkitTapHighlightColor: 'transparent',
                        touchAction: 'none',
                        cursor: 'grab',
                    }}
                >
                    <CardHeader className="px-2 py-0 mb-0 gap-1">
                        <div className='flex flex-row justify-between text-sm text-muted-foreground'>
                            <div className="flex items-center">
                                <Avatar
                                    className="h-6 w-6 mr-2 cursor-pointer"
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                        window.open(`/customer/${customer.id}`, "_blank")
                                    }}
                                >
                                    <AvatarFallback>{customerInitials}</AvatarFallback>
                                </Avatar>
                                <span
                                    className="truncate font-medium cursor-pointer hover:underline hover:text-blue-600"
                                    title={`${customer.firstName} ${customer.lastName}`}
                                    onPointerDown={(e) => e.stopPropagation()}
                                    onClick={() => window.open(`/customer/${customer.id}`, "_blank")}
                                >{customer.firstName} {customer.lastName}</span>
                            </div>

                            <div className="flex items-center gap-1">
                                {isUnder18 && (
                                    <TapTooltip
                                        trigger={
                                            <div className="relative flex items-center justify-center w-6 h-6 rounded-full bg-white border-2 border-red-600">
                                                <span className="text-red-600 text-sm font-bold">18</span>
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="w-full h-[1px] bg-red-600 rotate-45 origin-center"></div>
                                                </div>
                                            </div>
                                        }
                                    >
                                        Unter 18 Jahre alt ({customerAge} Jahre)
                                    </TapTooltip>
                                )}

                                {birthdayInfo && (
                                    <TapTooltip
                                        trigger={
                                            <div className="flex flex-col items-center">
                                                <Cake className="h-4 w-4 text-pink-500" />
                                                <span className="text-[8px] leading-tight text-pink-500 font-medium">
                                                    {birthdayInfo.daysAgo === 0 ? 'Heute' : `vor ${birthdayInfo.daysAgo}d`}
                                                </span>
                                            </div>
                                        }
                                    >
                                        {birthdayInfo.daysAgo === 0
                                            ? 'Hat heute Geburtstag! 🎂'
                                            : `Hatte vor ${birthdayInfo.daysAgo} ${birthdayInfo.daysAgo === 1 ? 'Tag' : 'Tagen'} Geburtstag`}
                                    </TapTooltip>
                                )}

                                {isAppointmentOldPrice(appointmentData.createdAt) && (
                                    <TapTooltip trigger={<Euro className="h-4 w-4 text-yellow-500" />}>
                                        Alter Preis
                                    </TapTooltip>
                                )}

                                {hasNegativeBalance && (
                                    <TapTooltip trigger={<HandCoins className="h-4 w-4 text-red-600" />}>
                                        Offener Saldo: {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(customerBalanceEur)}
                                    </TapTooltip>
                                )}

                                {hasActiveBundle && (
                                    <TapTooltip trigger={<Package className="h-4 w-4 text-emerald-600" />}>
                                        <div>Aktives Bundle</div>
                                        {activeBundleVouchers.map(v => (
                                            <div key={v.id}>{v.treatmentVariantName}: {v.remainingUnits}/{v.unitQuantity} übrig</div>
                                        ))}
                                    </TapTooltip>
                                )}

                                {hasAnyNote && (
                                    <TapTooltip trigger={<StickyNoteIcon className="h-4 w-4 text-amber-500" />}>
                                        Notiz vorhanden
                                    </TapTooltip>
                                )}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="px-2 py-0 text-xs text-[11px] space-y-2 mt-0">
                        {hasCompletedAnamneseQuestionnaire && !!latestQuestionnaireResponse ? (
                            <div className="flex items-center">
                                <QuestionnaireResponseModalBadge
                                    questionnaireResponseId={latestQuestionnaireResponse.id}
                                    createdAt={latestQuestionnaireResponse.createdAt}
                                />
                            </div>
                        ) : (
                            <div className="flex items-center">
                                <FileText className="h-3 w-3 mr-1.5 text-muted-foreground flex-shrink-0" />
                                <Badge variant='outline' className='py-0 bg-red-400 text-slate-900'>
                                    Keine Anamnese
                                </Badge>
                            </div>
                        )}
                        {hasCompletedSkinReport && skinReportsQuery.data?.[0]?.id ? (
                            <div className="flex items-center">
                                <ScanFace className="h-3 w-3 mr-1.5 text-muted-foreground flex-shrink-0" />
                                <Badge variant='outline' className='py-0 bg-green-100 text-green-900 underline cursor-pointer' onClick={() => {
                                    // Open new window _blank with treatment planner URL
                                    window.open(`https://portal-kalia-lab.vercel.app/dashboard/skin-reports/${skinReportsQuery.data?.[0]?.id}`, "_blank")
                                }}>
                                    Scan abgeschlossen
                                </Badge>
                            </div>
                        ) : (
                            <div className="flex items-center">
                                <ScanFace className="h-3 w-3 mr-1.5 text-muted-foreground flex-shrink-0" />
                                <Badge variant='outline' className='py-0 bg-red-400 text-slate-900'>
                                    Kein Scan
                                </Badge>
                            </div>
                        )}
                        {showDocStatusBadge && (() => {
                            if (docTreatments.length === 0) {
                                return (
                                    <div className="flex items-center">
                                        <Stethoscope className="h-3 w-3 mr-1.5 text-muted-foreground flex-shrink-0" />
                                        <Badge variant='outline' className='py-0 bg-slate-100 text-slate-900'>
                                            Keine Doku
                                        </Badge>
                                    </div>
                                );
                            }

                            return (
                                <div className="flex items-start">
                                    <Stethoscope className="h-3 w-3 mr-1.5 text-muted-foreground flex-shrink-0 mt-0.5" />
                                    <div>
                                        <Badge variant='outline' className='py-0 bg-green-100 text-green-900'>
                                            Leistung dokumentiert
                                        </Badge>
                                        <div className="text-[10px] text-muted-foreground mt-0.5">
                                            {Array.from(new Set(documentedNames)).join(', ')}
                                        </div>
                                        {pendingPractitioners.length > 0 && (
                                            <div className="text-[10px] text-orange-600 mt-0.5">
                                                noch ausstehend: {pendingPractitioners.map(p => p.name).join(', ')}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })()}
                        {liveBoardGroupStatus === AppointmentLiveBoardGroup.FINALIZING && (
                            <div className="flex items-center">
                                <Wallet className="h-3 w-3 mr-1.5 text-muted-foreground flex-shrink-0" />
                                {isPaid ? (
                                    <Badge variant='outline' className='py-0 bg-green-100 text-green-900'>
                                        Bezahlt
                                    </Badge>
                                ) : (
                                    <Badge variant='outline' className='py-0 bg-slate-100 text-slate-900'>
                                        Nicht Bezahlt
                                    </Badge>
                                )}
                            </div>
                        )}
                        <div className="flex items-center">
                            <ClockIcon className="h-3 w-3 mr-1.5 text-muted-foreground flex-shrink-0" />
                            <span className='leading-snug'>
                                {appointmentStart.format("HH:mm")} -{' '}
                                {appointmentEnd.format("HH:mm")}
                            </span>
                        </div>
                        {!!timeWaited && !!totalTimeInClinic && liveBoardGroupStatus !== AppointmentLiveBoardGroup.INITIAL && status !== AppointmentStatus.NO_SHOW && status !== AppointmentStatus.COMPLETED && (
                            <div className="flex items-center">
                                <Hourglass className="h-3 w-3 mr-1.5 text-muted-foreground flex-shrink-0" />
                                <span className='leading-snug'>
                                    {timeWaited} / {totalTimeInClinic}
                                </span>
                            </div>
                        )}
                        {variants.length > 0 && (
                            <div className="flex items-start">
                                <TagIcon className="h-3 w-3 mr-1 text-muted-foreground flex-shrink-0 mt-0.5" />
                                <div>
                                    {variants.map((variant) => (
                                        <Badge key={variant.id} variant="outline" className="text-xs text-[10px] mr-1 mb-1 !text-wrap !wrap-anywhere">
                                            {variant.name}
                                        </Badge>
                                    ))}
                                    {!!couponCodes && couponCodes.trim().length > 0 && (
                                        <Badge variant="outline" className="text-xs text-[10px] mr-1 mb-1 !text-wrap !wrap-anywhere">
                                            {couponCodes}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        )}
                        {practitioners.length > 0 && (
                            <div className="flex items-center text-muted-foreground">
                                <UsersIcon className="h-3 w-3 mr-1.5 flex-shrink-0" />
                                <span className="truncate" title={practitioners.map((p) => p.name).join(', ')}>
                                    {Array.from(new Set(practitioners.map((p) => p.name))).join(', ')}
                                </span>
                            </div>
                        )}
                        {rooms.length > 0 && (
                            <div className="flex items-center text-muted-foreground">
                                <HomeIcon className="h-3 w-3 mr-1.5 flex-shrink-0" />
                                <span className="truncate" title={rooms.map((r) => r.name).join(', ')}>
                                    {Array.from(new Set(rooms.map((r) => r.name))).join(', ')}
                                </span>
                            </div>
                        )}
                        {equipment.length > 0 && (
                            <div className="flex items-center text-muted-foreground">
                                <Cog className="h-3 w-3 mr-1.5 flex-shrink-0" />
                                <span className="truncate" title={equipment.map((e) => e.name).join(', ')}>
                                    {Array.from(new Set(equipment.map((e) => e.name))).join(', ')}
                                </span>
                            </div>
                        )}
                        {!!couponCodes && couponCodes.trim().length > 0 && (
                            <div className="flex items-center text-muted-foreground">
                                <GiftIcon className="h-3 w-3 mr-1.5 flex-shrink-0" />
                                <span className="truncate">
                                    {couponCodes}
                                </span>
                            </div>
                        )}

                        {appointmentData.liveBoardGroupStatus === AppointmentLiveBoardGroup.ARRIVED ? (
                            <SendAnamneseUrlDialog
                                customerId={appointmentData.customer.id}
                                appointmentId={appointmentData.id}
                                triggerClassName="flex-0 w-fit text-start h-min px-2 text-xs whitespace-pre-wrap"
                            />
                        ) : null}

                        <div className='w-full flex flex-row flex-wrap items-end gap-x-2 gap-y-4 2xl:gap-6 mt-4'>
                            {isTreatmentColumn && canCreateDocumentation ? (
                                latestTreatment && latestTreatment.administeredByPractitionerId === currentPractitionerId ? (
                                    <Button
                                        size="sm"
                                        variant={latestTreatment.documentationStep === 'ATTRIBUTION_STEP' ? undefined : 'outline'}
                                        className={
                                            latestTreatment.documentationStep === 'ATTRIBUTION_STEP'
                                                ? "flex-0 w-fit h-7 px-2 text-xs bg-yellow-500 hover:bg-yellow-600 text-black transition-all"
                                                : "flex-0 w-fit h-7 px-2 text-xs"
                                        }
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            docDialog.openDocDialog({
                                                mode: 'edit',
                                                customerId: appointmentData.customer.id,
                                                appointmentId: appointmentData.id,
                                                treatmentId: latestTreatment?.id,
                                            });
                                        }}
                                    >
                                        <Stethoscope className="h-4 w-4 mr-1" />
                                        {latestTreatment.documentationStep === 'ATTRIBUTION_STEP' ? 'Medizinische Doku starten' : 'Dokumentation bearbeiten'}
                                    </Button>
                                ) : (
                                    <Button
                                        size="sm"
                                        className="flex-0 w-fit h-7 px-2 text-xs bg-green-800 hover:bg-green-700 text-white transition-all"
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            docDialog.openDocDialog({
                                                mode: 'new',
                                                customerId: appointmentData.customer.id,
                                                appointmentId: appointmentData.id,
                                            });
                                        }}
                                    >
                                        <Stethoscope className="h-4 w-4 mr-1" />
                                        Leistung dokumentieren
                                    </Button>
                                )
                            ) : null}
                       
                            {appointmentData.liveBoardGroupStatus === AppointmentLiveBoardGroup.INITIAL && appointmentData.status !== AppointmentStatus.NO_SHOW ? (
                                <AlertDialog>
                                    <AlertDialogTrigger asChild>
                                        <Button
                                            size="sm"
                                            className="flex-0 w-fit h-7 px-2 text-xs bg-red-800 hover:bg-red-700 transition-all"
                                            disabled={markCompletedMutation.isPending || markNoShowMutation.isPending}
                                        >
                                            {markNoShowMutation.isPending && <Loader2 className='animate-spin' />}
                                            No-Show
                                        </Button>
                                    </AlertDialogTrigger>
                                    <AlertDialogContent>
                                        <AlertDialogHeader>
                                            <AlertDialogTitle>Bist du dir sicher?</AlertDialogTitle>
                                            <AlertDialogDescription>
                                                Bist du sicher, dass du diesen Termin auf NOSHOW setzen möchtest?
                                            </AlertDialogDescription>
                                        </AlertDialogHeader>
                                        <AlertDialogFooter>
                                            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                            <AlertDialogAction asChild>
                                                <Button
                                                    className="bg-red-800 hover:bg-red-700 transition-all"
                                                    onPointerDown={(e) => {
                                                        e.stopPropagation();
                                                    }}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        handleMarkNoshow();
                                                    }}
                                                    disabled={markCompletedMutation.isPending || markNoShowMutation.isPending}
                                                >
                                                    {markNoShowMutation.isPending && <Loader2 className='animate-spin' />}
                                                    Mark No-Show
                                                </Button>
                                            </AlertDialogAction>
                                        </AlertDialogFooter>
                                    </AlertDialogContent>
                                </AlertDialog>
                            ) : null}
                            {appointmentData.liveBoardGroupStatus === AppointmentLiveBoardGroup.FINALIZING && appointmentData.status !== AppointmentStatus.COMPLETED ? (
                                <div className='flex flex-col gap-4 '>
                                    <div className='flex items-center gap-2'>
                                        <Button
                                            variant={isPaid && !(!hasBillingDraft && hasMissingDocumentation) ? 'outline' : undefined}
                                            size="sm"
                                            className={
                                                !hasBillingDraft && hasMissingDocumentation
                                                    ? "h-7 max-h-none px-2 text-xs flex-1 bg-yellow-500 hover:bg-yellow-600 text-black transition-all"
                                                    : isPaid ? "h-7 px-2 text-xs flex-1" : "h-7 max-h-none px-2 text-xs flex-1 bg-green-800 hover:bg-green-700 transition-all"
                                            }
                                            onPointerDown={(e) => e.stopPropagation()}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (hasBillingDraft) {
                                                    billingDraftDialog.openDetail(latestActiveBillingDraft.id, appointmentData.id);
                                                } else if (hasMissingDocumentation) {
                                                    setMissingDocWarningOpen(true);
                                                } else {
                                                    billingDraftDialog.openCreator(appointmentData.customer.id, appointmentData.id);
                                                }
                                            }}
                                        >
                                            <Wallet className="h-4 w-4 mr-2" />
                                            {hasBillingDraft ? 'Auftrag öffnen' : 'Auftrag erstellen'}
                                        </Button>
                                        {!hasBillingDraft && hasMissingDocumentation && (
                                            <span title={`Dokumentation ausstehend: ${pendingPractitionerNames.join(', ')}`}>
                                                <AlertTriangle className="h-4 w-4 text-yellow-500" />
                                            </span>
                                        )}
                                        <AlertDialog open={missingDocWarningOpen} onOpenChange={setMissingDocWarningOpen}>
                                            <AlertDialogContent>
                                                <AlertDialogHeader>
                                                    <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-md">
                                                        <AlertDialogTitle className="flex items-center gap-2 text-amber-800">
                                                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                                                            Leistung noch nicht dokumentiert
                                                        </AlertDialogTitle>
                                                        <AlertDialogDescription className="mt-2 text-amber-700">
                                                            Für diesen Termin wurde die Leistung noch nicht von allen Behandlern dokumentiert. Bitte lass die Leistung zuerst dokumentieren, bevor du den Auftrag erstellst.
                                                        </AlertDialogDescription>
                                                        <div className="mt-3 text-sm text-amber-800">
                                                            <div className="font-medium">Dokumentation ausstehend:</div>
                                                            <ul className="list-disc pl-5 mt-1">
                                                                {pendingPractitionerNames.map((name) => (
                                                                    <li key={name}>{name}</li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    </div>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <Button
                                                        className="bg-red-800 hover:bg-red-700 transition-all"
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setMissingDocWarningOpen(false);
                                                            billingDraftDialog.openCreator(appointmentData.customer.id, appointmentData.id);
                                                        }}
                                                    >
                                                        Trotzdem Auftrag erstellen
                                                    </Button>
                                                    <Button
                                                        className="bg-green-800 hover:bg-green-700 transition-all"
                                                        onPointerDown={(e) => e.stopPropagation()}
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setMissingDocWarningOpen(false);
                                                        }}
                                                    >
                                                        OK
                                                    </Button>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </div>
                                    <div>
                                        {!hasBillingDraft || hasPendingDraft ? (
                                            <AlertDialog>
                                                <AlertDialogTrigger asChild>
                                                    <Button
                                                        variant={isPaid ? undefined : 'outline'}
                                                        size="sm"
                                                        className={isPaid ? "h-7 max-h-none px-2 text-xs flex-1 bg-green-800 hover:bg-green-700 transition-all" : "h-7 max-h-none px-2 text-xs flex-1"}
                                                        onPointerDown={(e) => {
                                                            e.stopPropagation();
                                                        }}
                                                        disabled={markCompletedMutation.isPending || markNoShowMutation.isPending}
                                                    >
                                                        {markCompletedMutation.isPending && <Loader2 className='animate-spin' />}
                                                        Termin Abschließen
                                                    </Button>
                                                </AlertDialogTrigger>
                                                <AlertDialogContent>
                                                    <AlertDialogHeader>
                                                        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-md">
                                                            <AlertDialogTitle className="flex items-center gap-2 text-amber-800">
                                                                <AlertTriangle className="h-5 w-5 text-amber-500" />
                                                                Warnung
                                                            </AlertDialogTitle>
                                                            <AlertDialogDescription className="mt-2 text-amber-700">
                                                                {!hasBillingDraft
                                                                    ? 'Es liegt noch kein Auftrag für den Termin vor. Bist du dir sicher, dass du den Termin abschließen möchtest?'
                                                                    : 'Der Termin hat mindestens einen nicht vollständig bezahlten Auftrag. Bist du dir sicher, dass du den Termin abschließen möchtest?'}
                                                            </AlertDialogDescription>
                                                        </div>
                                                    </AlertDialogHeader>
                                                    <AlertDialogFooter>
                                                        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                                        <AlertDialogAction asChild>
                                                            <Button
                                                                className="bg-red-700 hover:bg-red-600 transition-all"
                                                                onPointerDown={(e) => {
                                                                    e.stopPropagation();
                                                                }}
                                                                onClick={(e) => {
                                                                    e.preventDefault();
                                                                    e.stopPropagation();
                                                                    handleMarkCompleted();
                                                                }}
                                                                disabled={markCompletedMutation.isPending || markNoShowMutation.isPending}
                                                            >
                                                                {markCompletedMutation.isPending && <Loader2 className='animate-spin' />}
                                                                Trotzdem abschließen
                                                            </Button>
                                                        </AlertDialogAction>
                                                    </AlertDialogFooter>
                                                </AlertDialogContent>
                                            </AlertDialog>
                                        ) : (
                                            <Button
                                                variant={isPaid ? undefined : 'outline'}
                                                size="sm"
                                                className={isPaid ? "h-7 max-h-none px-2 text-xs flex-1 bg-green-800 hover:bg-green-700 transition-all" : "h-7 max-h-none px-2 text-xs flex-1"}
                                                onPointerDown={(e) => {
                                                    e.stopPropagation();
                                                }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleMarkCompleted();
                                                }}
                                                disabled={markCompletedMutation.isPending || markNoShowMutation.isPending}
                                            >
                                                {markCompletedMutation.isPending && <Loader2 className='animate-spin' />}
                                                Termin Abschließen
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            ) : null}

                            <div className='ml-auto flex shrink-0 items-end gap-1.5 2xl:gap-2'>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            size="icon"
                                            variant='outline'
                                            className={`relative w-9 h-9 2xl:w-11 2xl:h-11 p-0 ${chatUnreadCount > 0 ? 'border-red-500 text-red-600' : chatTotalCount > 0 ? 'border-primary-300 text-primary-600 bg-primary-50' : ''}`}
                                            onPointerDown={(e) => {
                                                e.stopPropagation();
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                appointmentChat.openChat({
                                                    appointmentId: appointmentData.id,
                                                    customerName: `${customer.firstName} ${customer.lastName}`,
                                                    appointmentTimeLabel: `${appointmentStart.format("HH:mm")} - ${appointmentEnd.format("HH:mm")}`,
                                                });
                                            }}
                                        >
                                            <MessageCircle className={chatUnreadCount > 0 ? 'fill-red-100' : chatTotalCount > 0 ? 'fill-primary-100' : undefined} />
                                            {chatUnreadCount > 0 && (
                                                <span className='absolute -top-2 -right-2 min-w-5 h-5 px-1 rounded-full bg-red-600 flex items-center justify-center text-[10px] text-white font-semibold'>
                                                    {chatUnreadCount > 9 ? '9+' : chatUnreadCount}
                                                </span>
                                            )}
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {chatTotalCount === 0
                                            ? 'Termin-Chat öffnen'
                                            : chatUnreadCount > 0
                                                ? `${chatUnreadCount} ungelesene ${chatUnreadCount === 1 ? 'Nachricht' : 'Nachrichten'}`
                                                : `${chatTotalCount} ${chatTotalCount === 1 ? 'Nachricht' : 'Nachrichten'}`}
                                    </TooltipContent>
                                </Tooltip>
                                <Button
                                    size="icon"
                                    variant='outline'
                                    className="w-9 h-9 2xl:w-11 2xl:h-11 p-0 text-xs"
                                    onPointerDown={(e) => {
                                        e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                        if (!appointmentData?.id) {
                                            return;
                                        }
                                        setMode('modify');
                                        setAppointmentId(appointmentData.id);
                                        setNotifyCustomer(false);
                                        setCreateOrModifyAppointmentDrawerOpen(true);
                                    }}
                                    disabled={markCompletedMutation.isPending || markNoShowMutation.isPending}
                                >
                                    <Eye />
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </>
        );
    }

    return <div className='h-[50px] flex items-center justify-center m-1 bg-white rounded-sm py-2 shadow-md text-base border border-gray-100'>{title}</div>;
});
Item.displayName = "Item";



type SortableItemProps = {
    id: UniqueIdentifier;
    children?: React.ReactNode | React.ReactNode[];
    style?: React.CSSProperties;
    disabled?: boolean;
}
const SortableItem = React.memo(({ id, children, style: propStyle, disabled }: SortableItemProps) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id,
        disabled: disabled ?? false,
    });

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        position: "relative",
        opacity: isDragging ? 0.8 : 1,
        cursor: disabled ? 'default' : 'grab',
        ...propStyle,
    };

    return (
        <div ref={setNodeRef} style={style} {...attributes} {...(disabled ? {} : listeners)}>
            {children}
        </div>
    );
});
SortableItem.displayName = "SortableItem";

const baseContainerStyle: React.CSSProperties = {
    padding: "0px 0px 0px",
    flexGrow: 1,
    alignSelf: "stretch",
    minHeight: 50,
};

const enhancedH3Style: React.CSSProperties = {
    textAlign: 'center',
    margin: '0 0 10px 0',
    padding: '10px 10px 10px 10px',
    width: '100%',
    flexShrink: 0,
    fontWeight: 600,
    fontSize: '1.0rem',
    color: '#374151',
    letterSpacing: '0.01em',
    borderBottom: '1px solid #e2e8f0',
    backgroundColor: 'rgba(0,0,0,0.02)',
};

type ContainerProps = {
    direction: 'row' | 'column';
    children?: React.ReactNode | React.ReactNode[];
    style?: React.CSSProperties;
    title?: string;
    color?: string;
    isCollapsible?: boolean;
    defaultCollapsed?: boolean;
    numItems?: number;
}
const Container = React.memo(React.forwardRef<HTMLDivElement, ContainerProps>((props, ref) => {
    const { children, direction, style: containerOuterStyle = {}, title, color, isCollapsible, defaultCollapsed, numItems } = props;

    const [isCollapsed, setIsCollapsed] = useState<boolean>(isCollapsible === true && defaultCollapsed !== undefined ? defaultCollapsed : false);

    return (
        <div
            ref={ref}
            style={{
                ...baseContainerStyle,
                ...containerOuterStyle,
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                className='relative w-full flex flex-row justify-start items-center px-2 py-2 gap-1'
            >
                <div className='absolute w-full h-full top-0 left-0 opacity-5 z-0' style={{ background: color }} />
                {isCollapsible === true && (
                    <Button variant='ghost' onClick={() => setIsCollapsed(!isCollapsed)} className='z-[1] w-min h-min !px-0'>
                        {isCollapsed ? <ChevronUp className='w-4 h-4 p-0' /> : <ChevronDown className='w-4 h-4 p-0' />}
                    </Button>
                )}
                {title && (
                    <div className='flex flex-row items-center gap-2'>
                        <Badge variant='outline' className='shrink-0 grow-0 max-w-full relative z-[1] text-black/80 font-medium py-0'>
                            <div className='absolute w-full h-full top-0 left-0 opacity-15 z-0' style={{ background: color }} />
                            <span className='z-[1] text-sm truncate'>
                                {title}
                            </span>
                            <span className='z-[1] text-sm text-muted-foreground'>{numItems}</span>
                        </Badge>
                    </div>
                )}
            </div>
            <div
                style={{
                    display: 'flex',
                    visibility: isCollapsed ? 'hidden' : 'visible',
                    flexDirection: direction,
                    flexGrow: 1,
                    width: '100%',
                    minHeight: '60px',
                    maxHeight: isCollapsed ? '60px' : 'none',
                    overflowY: 'auto',
                }}
                className='px-0 py-2 items-center'
            >
                {children}
            </div>
        </div>
    );
}));
Container.displayName = "Container";


type SortableContainerProps = {
    id: UniqueIdentifier;
    title: string;
    color?: string;
    getItems: (parentId?: UniqueIdentifier) => ItemType[];
    childLayout: 'horizontal' | 'vertical';
    style?: React.CSSProperties;
    allItems: ItemType[];
    activeDraggedItemId: UniqueIdentifier | null;
    findItem: (items: ItemType[], id: UniqueIdentifier | null | undefined) => ItemType | undefined;
    getItemDepth: (items: ItemType[], itemId: UniqueIdentifier | undefined) => number;
    getChildrenOfItem: (itemsList: ItemType[], parentId: UniqueIdentifier) => ItemType[];
    isCollapsible?: boolean;
    defaultCollapsed?: boolean;
};

const baseBoxShadowRoot = "0 5px 15px -3px rgba(0,0,0,0.07), 0 4px 6px -4px rgba(0,0,0,0.05)";
const baseBoxShadowNested = "0 3px 10px -2px rgba(0,0,0,0.05), 0 2px 4px -3px rgba(0,0,0,0.04)";
const activeBoxShadowHighlight = "0 0 0 3px rgba(59, 130, 246, 0.3)";

const SortableContainer = React.memo((props: SortableContainerProps) => {
    const {
        getItems, id, childLayout, style: parentPassedStyle = {}, title, color,
        allItems, activeDraggedItemId, findItem, getItemDepth, getChildrenOfItem,
        isCollapsible, defaultCollapsed,
    } = props;

    const items = getItems(id);
    const itemIds = useMemo(() => items.map((item) => item.id), [items]);

    const { isOver: isGeometricallyOver, setNodeRef } = useDroppable({ id });

    const amIValidDropTarget = React.useMemo(() => {
        if (!activeDraggedItemId || !isGeometricallyOver) return false;
        const draggedItemDetails = findItem(allItems, activeDraggedItemId);
        if (!draggedItemDetails || draggedItemDetails.container) return false;
        const thisContainerDetails = findItem(allItems, id);
        if (thisContainerDetails && thisContainerDetails.container && thisContainerDetails.childrenLayout === 'vertical') {
            const childrenOfThisContainer = getChildrenOfItem(allItems, thisContainerDetails.id);
            const thisContainerHasSubContainers = childrenOfThisContainer.some(child => child.container);
            const thisContainerDepth = getItemDepth(allItems, thisContainerDetails.id);
            return !thisContainerHasSubContainers && thisContainerDepth <= 1;
        }
        return false;
    }, [activeDraggedItemId, isGeometricallyOver, id, allItems, findItem, getItemDepth, getChildrenOfItem]);


    const depth = useMemo(() => getItemDepth(allItems, id), [allItems, id, getItemDepth]);
    const isRootColumn = depth === 0;

    const sortableItemWrapperStyle: React.CSSProperties = useMemo(() => ({
        width: '100%',
        flex: isRootColumn ? '1 1 0' : undefined,
        minWidth: isRootColumn ? 0 : undefined,
        ...parentPassedStyle,
    }), [isRootColumn, parentPassedStyle]);

    const containerVisualStyling: React.CSSProperties = useMemo(() => {
        const baseShadow = isRootColumn ? baseBoxShadowRoot : baseBoxShadowNested;
        return {
            backgroundColor: isRootColumn
                ? "#f8fafc"
                : "#f8fafc",
            borderRadius: "12px",
            borderWidth: "1px",
            borderStyle: "solid",
            borderColor: amIValidDropTarget ? "rgba(59, 130, 246, 0.5)" : "#e2e8f0",
            boxShadow: amIValidDropTarget ? `${activeBoxShadowHighlight}, ${baseShadow}` : baseShadow,
        };
    }, [isRootColumn, amIValidDropTarget]);

    return (
        <SortableItem id={id} style={sortableItemWrapperStyle} disabled={true}>
            <Container
                ref={setNodeRef}
                direction={childLayout === 'horizontal' ? 'row' : 'column'}
                title={title}
                color={color}
                style={containerVisualStyling}
                isCollapsible={isCollapsible}
                defaultCollapsed={defaultCollapsed}
                numItems={items.filter((item) => !item.container).length + items.reduce((current, item) => current + getItems(item.id).length, 0)}
            >
                <SortableContext
                    items={itemIds}
                    strategy={
                        childLayout === 'horizontal'
                            ? horizontalListSortingStrategy
                            : verticalListSortingStrategy
                    }
                >
                    {Array.from({ length: 1 }).map(_ => {
                        let renderedItems = 0;
                        const limitItemsIds = [
                            'upcoming'
                        ];

                        return (
                            items.map((childItem) => {
                                if (childItem.container) {
                                    return (
                                        <SortableContainer
                                            key={childItem.id}
                                            id={childItem.id}
                                            title={childItem.title}
                                            color={childItem.color}
                                            getItems={getItems}
                                            childLayout={childItem.childrenLayout || 'vertical'}
                                            style={{ margin: '5px 0', minWidth: '100%', flexGrow: 1, flexBasis: 0 }}
                                            allItems={allItems}
                                            activeDraggedItemId={activeDraggedItemId}
                                            findItem={findItem}
                                            getItemDepth={getItemDepth}
                                            getChildrenOfItem={getChildrenOfItem}
                                            isCollapsible={childItem.isCollapsible}
                                            defaultCollapsed={childItem.defaultCollapsed}
                                        />
                                    );
                                }
                                if (limitItemsIds.includes(String(id)) && renderedItems >= 4) {
                                    return null;
                                }
                                renderedItems += 1;
                                return (
                                    <SortableItem key={childItem.id} id={childItem.id} disabled={false} style={{ width: '100%' }}>
                                        <Item
                                            id={childItem.id}
                                            title={childItem.title}
                                            appointmentData={childItem.appointmentData}
                                        />
                                    </SortableItem>
                                );
                            })
                        )
                    })}
                </SortableContext>
            </Container>
        </SortableItem>
    );
});

const initialKanbanData: ItemType[] = [
    {
        id: AppointmentLiveBoardGroup.INITIAL,
        title: "Heute",
        container: true,
        childrenLayout: 'vertical',
        // color: '#2196F3',
        color: '#888',
    },
    {
        id: `upcoming`,
        title: "Bevorstehend",
        parent: AppointmentLiveBoardGroup.INITIAL,
        container: true,
        isCollapsible: true,
        defaultCollapsed: false,
        childrenLayout: 'vertical',
        // color: '#4CAF50'
        color: '#888'
    },
    {
        id: `delayed`,
        title: "Verspätet",
        parent: AppointmentLiveBoardGroup.INITIAL,
        container: true,
        isCollapsible: true,
        defaultCollapsed: false,
        childrenLayout: 'vertical',
        // color: '#F44336'
        color: '#888'
    },
    {
        id: `noshow`,
        title: "NOSHOW",
        parent: AppointmentLiveBoardGroup.INITIAL,
        container: true,
        isCollapsible: true,
        defaultCollapsed: true,
        childrenLayout: 'vertical',
        // color: '#795548'
        color: '#888'
    },


    {
        id: AppointmentLiveBoardGroup.ARRIVED,
        title: "Empfang (Angekommen)",
        container: true,
        childrenLayout: 'vertical',
        // color: '#8BC34A'
        color: '#888'
    },
    // {
    //     id: AppointmentLiveBoardGroup.SKINSCANNER,
    //     title: "Skinscanner",
    //     container: true,
    //     childrenLayout: 'vertical',
    //     // color: '#9C27B0'
    //     color: '#888'
    // },
    {
        id: AppointmentLiveBoardGroup.PREPARATION,
        title: "Vorbereitung",
        container: true,
        childrenLayout: 'vertical',
        // color: '#00BCD4'
        color: '#888'
    },
    {
        id: AppointmentLiveBoardGroup.TREATMENT,
        title: "Behandlung",
        container: true,
        childrenLayout: 'vertical',
        // color: '#009688'
        color: '#888'
    },

    {
        id: AppointmentLiveBoardGroup.FINALIZING,
        title: "Ausgang / Bezahlen",
        container: true,
        childrenLayout: 'vertical',
        // color: '#8BC34A'
        color: '#888'
    },
    {
        id: `final`,
        title: "Ausgang / Bezahlen",
        parent: AppointmentLiveBoardGroup.FINALIZING,
        container: true,
        isCollapsible: true,
        defaultCollapsed: false,
        childrenLayout: 'vertical',
        // color: '#795548'
        color: '#888'
    },
    {
        id: `completed`,
        title: "COMPLETED",
        parent: AppointmentLiveBoardGroup.FINALIZING,
        container: true,
        isCollapsible: true,
        defaultCollapsed: true,
        childrenLayout: 'vertical',
        // color: '#795548'
        color: '#888'
    },
];

type DataType = { items: ItemType[]; };
type KanbanBoardProps = { storeId: number; }

const shipmentStatusConfig: Record<string, { label: string; variant: string }> = {
    pending: { label: 'Ausstehend', variant: 'warning' },
    in_transit: { label: 'In Transit', variant: 'default' },
    delivered: { label: 'Zugestellt', variant: 'success' },
    fault: { label: 'Fehler', variant: 'destructive' },
};

const BlutlaborPickupIndicator = ({ storeId }: { storeId: number }) => {
    const { data, isLoading } = trpc.bloodTests.getTodayPickupInfoForStore.useQuery({ storeId });

    if (isLoading || !data) {
        return null;
    }

    const { shipment, shipmentUnavailable, pendingCount, confirmedCount, bloodDrawnCount, shipmentPickedUpCount } = data;

    const hasAnyCounts = pendingCount > 0 || confirmedCount > 0 || bloodDrawnCount > 0 || shipmentPickedUpCount > 0;

    return (
        <div className="flex items-center gap-2 text-sm pb-1">
            <TestTube className="h-4 w-4 text-muted-foreground shrink-0" />
            <span className="font-medium whitespace-nowrap">Blutlabor Pick-Up:</span>
            {shipmentUnavailable ? (
                <span className="text-muted-foreground">
                    Pick-Up-Status derzeit nicht verfügbar
                </span>
            ) : shipment ? (
                <div className="flex items-center gap-2">
                    <span className="text-green-600 font-semibold">Heute</span>
                    {shipment.pickupFrom && shipment.pickupTill && (
                        <span className="text-muted-foreground">
                            {dayjs(shipment.pickupFrom).format('HH:mm')} – {dayjs(shipment.pickupTill).format('HH:mm')}
                        </span>
                    )}
                    <Badge variant={(shipmentStatusConfig[shipment.status]?.variant ?? 'outline') as 'default'}>
                        {shipmentStatusConfig[shipment.status]?.label ?? shipment.status}
                    </Badge>
                </div>
            ) : (
                <span className="text-muted-foreground">
                    Heute noch kein Pick-Up gebucht (möglich bis 15 Uhr)
                </span>
            )}
            {hasAnyCounts && (
                <>
                    <Separator orientation="vertical" className="h-4 mx-1" />
                    <span className="font-medium whitespace-nowrap">Bluttests heute:</span>
                    {pendingCount > 0 && (
                        <Badge className="bg-yellow-100 text-yellow-700 border-0 text-xs">Ausstehend: {pendingCount}</Badge>
                    )}
                    {confirmedCount > 0 && (
                        <Badge className="bg-blue-100 text-blue-700 border-0 text-xs">Bestätigt: {confirmedCount}</Badge>
                    )}
                    {bloodDrawnCount > 0 && (
                        <Badge className="bg-green-100 text-green-700 border-0 text-xs">Blut abgenommen: {bloodDrawnCount}</Badge>
                    )}
                    {shipmentPickedUpCount > 0 && (
                        <Badge className="bg-indigo-100 text-indigo-700 border-0 text-xs">Abgeholt: {shipmentPickedUpCount}</Badge>
                    )}
                </>
            )}
        </div>
    );
};

const KanbanBoard = ({ storeId }: KanbanBoardProps) => {
    const { open: sidebarOpen } = useSidebar()
    const { timezone } = useUserStore(
        useShallow((state) => ({
            timezone: state.user?.timezone || 'Europe/Berlin',
        }))
    );
    const [isActuallyDragging, setIsActuallyDragging] = useState(false);
    const queryDate = useMemo(() => new Date().toUTCString(), []);

    const trpcUtils = trpc.useUtils();

    const roomsQuery = trpc.rooms.getAll.useQuery({ pageSize: 1000 });
    const rooms = useMemo(() => {
        const roomsList = roomsQuery.data?.data || [];
        return roomsList
            .filter((room) => room.storeId === storeId)
            .sort((a, b) => (a.sequence - b.sequence) || (a.id - b.id));
    }, [roomsQuery.data?.data, storeId]);

    const waitingRoomsQuery = trpc.waitingRooms.getAll.useQuery({ pageSize: 1000 });
    const waitingRooms = useMemo(() => {
        const waitingRoomsList = waitingRoomsQuery.data?.data || [];
        return waitingRoomsList.filter((room) => room.storeId === storeId);
    }, [waitingRoomsQuery.data?.data, storeId]);

    const { data: appointmentsData } = trpc.appointments.getAppointmentsForBoard.useQuery(
        { storeId: storeId, date: queryDate },
        {
            enabled: !!storeId,
            placeholderData: keepPreviousData,
            refetchInterval: isActuallyDragging ? false : 5 * 1000,
        }
    );

    const addLiveBoardGroupEntryMutation = trpc.appointments.addLiveBoardGroupEntry.useMutation({
        onSuccess: (_data, variables) => {
            console.log("Live board entry successfully updated for appointment:", variables.appointmentId);
            trpcUtils.appointments.getAppointmentsForBoard.invalidate({
                storeId: storeId,
                date: queryDate
            });
        },
        onError: (error, variables) => {
            console.log("Failed to update live board entry for appointment:", variables.appointmentId, error);
            toast.error("Failed to update live board entry for appointment", {
                description: error.message
            })
        }
    });

    const updateAppointmentStatusMutation = trpc.appointments.updateStatus.useMutation({
        onSuccess: (_data, variables) => {
            console.log("Live board entry successfully updated for appointment:", variables.appointmentId);
            trpcUtils.appointments.getAppointmentsForBoard.invalidate({
                storeId: storeId,
                date: queryDate
            });
        },
        onError: (error, variables) => {
            console.log("Failed to update live board entry for appointment:", variables.appointmentId, error);
            toast.error("Failed to update live board entry for appointment", {
                description: error.message
            })
        }
    })

    const [searchQuery, setSearchQuery] = useState<string>("");

    const [billingDraftCreator, setBillingDraftCreator] = useState<{ open: boolean; customerId: number; appointmentId: string }>({ open: false, customerId: 0, appointmentId: '' });
    const [billingDraftDetail, setBillingDraftDetail] = useState<{ open: boolean; billingDraftId: string; appointmentId: string }>({ open: false, billingDraftId: '', appointmentId: '' });

    const billingDraftDialogContextValue = useMemo<BillingDraftDialogContextType>(() => ({
        openCreator: (customerId, appointmentId) => {
            setBillingDraftCreator({ open: true, customerId, appointmentId });
        },
        openDetail: (billingDraftId, appointmentId) => {
            setBillingDraftDetail({ open: true, billingDraftId, appointmentId });
        },
    }), []);

    const [docDialogState, setDocDialogState] = useState<DocDialogState | null>(null);

    const docDialogContextValue = useMemo<DocDialogContextType>(() => ({
        openDocDialog: (params) => setDocDialogState(params),
    }), []);

    const [chatTarget, setChatTarget] = useState<AppointmentChatTarget | null>(null);

    const boardAppointmentIds = useMemo(
        () => (appointmentsData || []).map((appointment) => appointment.id).sort(),
        [appointmentsData]
    );

    const { data: chatCountsData } = trpc.appointmentChat.getUnreadCounts.useQuery(
        { appointmentIds: boardAppointmentIds },
        {
            enabled: boardAppointmentIds.length > 0,
            placeholderData: keepPreviousData,
            refetchInterval: isActuallyDragging ? false : 5 * 1000,
        }
    );

    const appointmentChatContextValue = useMemo<AppointmentChatContextType>(() => ({
        openChat: (target) => setChatTarget(target),
        unreadCounts: chatCountsData?.unreadCounts ?? {},
        totalCounts: chatCountsData?.totalCounts ?? {},
    }), [chatCountsData]);

    const [data, setData] = useState<DataType>({ items: initialKanbanData });
    const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

    useEffect(() => {
        if (!appointmentsData && !rooms.length && !waitingRooms.length) {
            return;
        }

        const preparationRoomItems: ItemType[] = rooms
            .map((room) => ({
                id: `preparation-room-${room.id}`,
                title: room.name,
                parent: AppointmentLiveBoardGroup.PREPARATION,
                container: true,
                childrenLayout: 'vertical',
            })) as ItemType[];
        const treatmentRoomItems: ItemType[] = rooms
            .map((room) => ({
                id: `treatment-room-${room.id}`,
                title: room.name,
                parent: AppointmentLiveBoardGroup.TREATMENT,
                container: true,
                childrenLayout: 'vertical',
            })) as ItemType[];
        const waitingRoomItems: ItemType[] = waitingRooms
            .map((room) => ({
                id: `waiting-room-${room.id}`,
                title: room.name,
                parent: AppointmentLiveBoardGroup.ARRIVED,
                container: true,
                childrenLayout: 'vertical',
            }))
            .sort((a, b) => {
                const roomA = waitingRooms.find((r) => `waiting-room-${r.id}` === a.id);
                const roomB = waitingRooms.find((r) => `waiting-room-${r.id}` === b.id);
                return (roomA?.sequence ?? 1) - (roomB?.sequence ?? 1);
            }) as ItemType[];

        const rawAppointmentItems: ItemType[] = (appointmentsData || []).map((appointment) => {
            const liveGroup = appointment.liveBoardGroupStatus;
            const roomId = appointment.liveRoomId;
            const waitingRoomId = appointment.liveWaitingRoomId;
            const startTime = LocalDateTime.deserialize(appointment.startTimeSerialized);
            const endTime = LocalDateTime.deserialize(appointment.endTimeSerialized);
            let parentId: UniqueIdentifier | undefined;
            if (appointment.status === AppointmentStatus.NO_SHOW) {
                parentId = 'noshow';
            } else if (appointment.status === AppointmentStatus.COMPLETED) {
                parentId = 'completed';
            } else {
                switch (liveGroup) {
                    case AppointmentLiveBoardGroup.INITIAL:
                        const minutesThreshold = 30;
                        const appointmentStart = startTime.toUTC();
                        const thirtyMinutesAgo = new Date(Date.now() - minutesThreshold * 60 * 1000);
                        const isCustomerDelayed = appointmentStart.getTime() < thirtyMinutesAgo.getTime();
                        if (isCustomerDelayed) {
                            parentId = 'delayed';
                        } else {
                            parentId = 'upcoming';
                        }
                        break;
                    case AppointmentLiveBoardGroup.ARRIVED: parentId = waitingRoomId ? `waiting-room-${waitingRoomId}` : AppointmentLiveBoardGroup.ARRIVED; break;
                    case AppointmentLiveBoardGroup.SKINSCANNER: parentId = AppointmentLiveBoardGroup.SKINSCANNER; break;
                    case AppointmentLiveBoardGroup.PREPARATION: parentId = roomId ? `preparation-room-${roomId}` : AppointmentLiveBoardGroup.PREPARATION; break;
                    case AppointmentLiveBoardGroup.TREATMENT: parentId = roomId ? `treatment-room-${roomId}` : AppointmentLiveBoardGroup.TREATMENT; break;
                    case AppointmentLiveBoardGroup.FINALIZING:
                        parentId = 'final';
                        break;
                    default: parentId = AppointmentLiveBoardGroup.INITIAL;
                }
            }
            return {
                id: `appointment-${appointment.id}`,
                title: appointment.title,
                parent: parentId,
                appointmentData: {
                    ...appointment,
                    createdAt: new Date(appointment.createdAt),
                    startTime,
                    endTime,
                    customer: {
                        ...appointment.customer,
                        latestQuestionnaireResponse: appointment.customer.questionnaireResponses.length > 0 ? {
                            id: appointment.customer.questionnaireResponses[0].id,
                            createdAt: new Date(appointment.customer.questionnaireResponses[0].createdAt),
                        } : null,
                    },
                } as AppointmentData,
            };
        }).filter((appointmentItem) => {
            if (searchQuery.trim().length === 0) {
                return true;
            }
            const appointmentData = appointmentItem.appointmentData;
            const searchTags = [
                appointmentData.title.toLowerCase(),
                appointmentData.customer.firstName.toLowerCase() + ' ' + appointmentData.customer.lastName.toLowerCase(),
                ...appointmentData.practitioners.map((p) => p.name.toLowerCase()),
                ...appointmentData.rooms.map((r) => r.name.toLowerCase()),
                ...appointmentData.equipment.map((e) => e.name.toLowerCase()),
            ]
            if (appointmentData.customer.email) {
                searchTags.push(appointmentData.customer.email.toLowerCase());
            }
            if (appointmentData.customer.phone) {
                searchTags.push(appointmentData.customer.phone.toLowerCase());
            }
            return searchTags.some((tag) => tag.includes(searchQuery.toLowerCase()))
        });

        const groupedAppointments: Record<UniqueIdentifier, ItemType[]> = {};
        rawAppointmentItems.forEach(item => {
            const parent = item.parent!;
            if (!groupedAppointments[parent]) {
                groupedAppointments[parent] = [];
            }
            groupedAppointments[parent].push(item);
        });

        for (const parentId in groupedAppointments) {
            groupedAppointments[parentId].sort((a, b) => {
                if (!a.appointmentData || !b.appointmentData) return 0;

                if (parentId === 'final') {
                    const aEntry = a.appointmentData.liveBoardGroupEntryCreatedTimeUtc;
                    const bEntry = b.appointmentData.liveBoardGroupEntryCreatedTimeUtc;
                    if (aEntry && bEntry) {
                        return new Date(bEntry).getTime() - new Date(aEntry).getTime();
                    }
                    return 0;
                }

                return a.appointmentData.startTime.toUTC().getTime() - b.appointmentData.startTime.toUTC().getTime();
            });
        }
        const appointmentItems: ItemType[] = Object.values(groupedAppointments).flat();

        const newItems = [...initialKanbanData, ...waitingRoomItems, ...preparationRoomItems, ...treatmentRoomItems, ...appointmentItems];
        setData({ items: newItems });
    }, [rooms, appointmentsData, searchQuery, waitingRooms, timezone]);

    const sensors = useSensors(
        useSensor(DialogAwareMouseSensor, {
            activationConstraint: {
                distance: 8,
            },
        }),
        useSensor(DialogAwareTouchSensor, {
            activationConstraint: {
                delay: 100,
                tolerance: 5,
            },
        }),
        useSensor(DialogAwareKeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const findItemMemoized = useCallback((items: ItemType[], id: UniqueIdentifier | null | undefined): ItemType | undefined => {
        if (!id) return undefined;
        return items.find((item) => item.id === id);
    }, []);

    const getItemDepthMemoized = useCallback((items: ItemType[], itemId: UniqueIdentifier | undefined): number => {
        let depth = 0;
        let currentItemId = itemId;
        while (currentItemId) {
            const currentItem = items.find(item => item.id === currentItemId);
            if (!currentItem?.parent) break;
            depth++;
            currentItemId = currentItem.parent;
        }
        return depth;
    }, []);

    const getChildrenOf = useCallback((itemsList: ItemType[], parentId: UniqueIdentifier): ItemType[] => {
        return itemsList.filter(item => item.parent === parentId);
    }, []);

    const getItems = useMemo(() => {
        return (parentId?: UniqueIdentifier): ItemType[] => {
            return data.items.filter((item) => item.parent === parentId);
        };
    }, [data.items]);

    const rootItemIds = useMemo(() => getItems(undefined).map(item => item.id), [data.items]);

    const getDragOverlay = useCallback((): React.ReactNode => {
        if (!activeId) return null;
        const activeItem = findItemMemoized(data.items, activeId);
        if (!activeItem || activeItem.container) return null;
        return <Item id={activeItem.id} title={activeItem.title} appointmentData={activeItem.appointmentData} />;
    }, [activeId, data.items, findItemMemoized]);


    const handleDragStart = useCallback((event: DragStartEvent): void => {
        const itemBeingDragged = findItemMemoized(data.items, event.active.id);
        if (itemBeingDragged && !itemBeingDragged.container) {
            setActiveId(event.active.id);
            setIsActuallyDragging(true);
        } else {
            setActiveId(null);
        }
    }, [data.items, findItemMemoized, setIsActuallyDragging]);

    const handleDragOver = useCallback((event: DragOverEvent): void => {
        const { active, over } = event;
        const currentActiveId = active.id;
        const overId = over?.id;

        if (!overId || !currentActiveId || currentActiveId === overId) return;

        // Use requestAnimationFrame to throttle updates
        requestAnimationFrame(() => {
            setData((prev) => {
                const items = [...prev.items];
                const activeItemIndex = items.findIndex(item => item.id === currentActiveId);
                const activeItem = items[activeItemIndex];
                const overItemData = items.find(item => item.id === overId);

                if (!activeItem || activeItem.container || activeItemIndex === -1) return prev;

                let newParentIdCandidate: UniqueIdentifier | undefined;

                if (overItemData?.container) {
                    newParentIdCandidate = overId;
                } else if (overItemData) {
                    newParentIdCandidate = overItemData.parent;
                } else {
                    return prev;
                }

                if (newParentIdCandidate === undefined) return prev;

                const newParentContainer = items.find(item => item.id === newParentIdCandidate);
                if (!newParentContainer || !newParentContainer.container || newParentContainer.childrenLayout !== 'vertical') {
                    return prev;
                }

                const childrenOfNewParent = items.filter(item => item.parent === newParentContainer.id);
                const newParentHasSubContainers = childrenOfNewParent.some(child => child.container);
                if (newParentHasSubContainers) return prev;

                let parentDepth = 0;
                let currentParentId = newParentContainer.parent;
                while (currentParentId) {
                    const parentItem = items.find(item => item.id === currentParentId);
                    if (!parentItem) break;
                    parentDepth++;
                    currentParentId = parentItem.parent;
                }
                if (parentDepth > 1) return prev;

                const oldParentId = activeItem.parent;
                const parentChanged = oldParentId !== newParentIdCandidate;

                if (!parentChanged) {
                    if (overItemData && overItemData.parent === newParentIdCandidate && overItemData.id !== currentActiveId) {
                        const itemsInParent = items.filter(item => item.parent === newParentIdCandidate);
                        const oldIndex = itemsInParent.findIndex(item => item.id === currentActiveId);
                        const newIndex = itemsInParent.findIndex(item => item.id === overItemData.id);
                        if (oldIndex === newIndex) return prev;
                    } else {
                        return prev;
                    }
                }

                const updatedItems = items.map(item =>
                    item.id === currentActiveId
                        ? { ...item, parent: newParentIdCandidate }
                        : item
                );

                if (overItemData && overItemData.parent === newParentIdCandidate && overItemData.id !== currentActiveId) {
                    const itemsInNewParent = updatedItems.filter(item => item.parent === newParentIdCandidate);
                    const oldIndexInNewParent = itemsInNewParent.findIndex(item => item.id === currentActiveId);
                    const newIndexInNewParent = itemsInNewParent.findIndex(item => item.id === overItemData.id);

                    if (oldIndexInNewParent !== -1 && newIndexInNewParent !== -1 && oldIndexInNewParent !== newIndexInNewParent) {
                        const reorderedParentGroup = arrayMove(itemsInNewParent, oldIndexInNewParent, newIndexInNewParent);
                        const finalItems = updatedItems.filter(item => item.parent !== newParentIdCandidate);
                        return { items: [...finalItems, ...reorderedParentGroup] };
                    }
                }

                return { items: updatedItems };
            });
        });
    }, []);

    const handleDragEnd = useCallback((event: DragEndEvent): void => {
        setActiveId(null);
        setIsActuallyDragging(false);

        const { active, over } = event;
        if (!over || !active.id) return;

        const finalActiveItemState = findItemMemoized(data.items, active.id);
        const finalParentContainerState = findItemMemoized(data.items, finalActiveItemState?.parent);

        if (finalActiveItemState && finalActiveItemState.appointmentData && finalParentContainerState) {
            const finalContainerStateId = String(finalParentContainerState.id);
            const appointmentData = finalActiveItemState.appointmentData;

            const oldStatus = appointmentData.status;
            const oldGroup = appointmentData.liveBoardGroupStatus;
            const oldRoomId = appointmentData.liveRoomId ?? undefined;
            const oldWaitingRoomId = appointmentData.liveWaitingRoomId ?? undefined;

            const isNewParentWaitingRoom = finalContainerStateId.includes("waiting-room-");
            const isNewParentPreparationRoom = finalContainerStateId.includes("preparation-room-");
            const isNewParentTreatmentRoom = finalContainerStateId.includes("treatment-room-");

            let newEffectiveGroup: AppointmentLiveBoardGroup;
            let newRoomId: number | undefined = undefined;
            let newWaitingRoomId: string | undefined = undefined;
            if (finalContainerStateId.includes("delayed") || finalContainerStateId.includes("upcoming") || finalContainerStateId.includes("noshow")) {
                newEffectiveGroup = AppointmentLiveBoardGroup.INITIAL;
                if (finalContainerStateId.includes("noshow") && oldStatus !== AppointmentStatus.NO_SHOW) {
                    updateAppointmentStatusMutation.mutate({
                        appointmentId: appointmentData.id,
                        appointmentStatus: AppointmentStatus.NO_SHOW
                    })
                }
            } else if (isNewParentWaitingRoom) {
                newEffectiveGroup = AppointmentLiveBoardGroup.ARRIVED;
                newWaitingRoomId = finalContainerStateId.replace("waiting-room-", "");
            } else if (isNewParentPreparationRoom) {
                newEffectiveGroup = AppointmentLiveBoardGroup.PREPARATION;
                newRoomId = parseInt(finalContainerStateId.replace("preparation-room-", ""));
            } else if (isNewParentTreatmentRoom) {
                newEffectiveGroup = AppointmentLiveBoardGroup.TREATMENT;
                newRoomId = parseInt(finalContainerStateId.replace("treatment-room-", ""));
            } else if (finalContainerStateId.includes("completed") || finalContainerStateId.includes("final")) {
                newEffectiveGroup = AppointmentLiveBoardGroup.FINALIZING;
                // if (finalContainerStateId.includes("completed") && oldStatus !== AppointmentStatus.COMPLETED) {
                //     updateAppointmentStatusMutation.mutate({
                //         appointmentId: appointmentData.id,
                //         appointmentStatus: AppointmentStatus.COMPLETED
                //     })
                // }
            } else {
                newEffectiveGroup = finalParentContainerState.id as AppointmentLiveBoardGroup;
            }

            const groupChanged = oldGroup !== newEffectiveGroup;
            const roomChanged = oldRoomId !== newRoomId;
            const waitingRoomChanged = oldWaitingRoomId !== newWaitingRoomId;

            if (!finalContainerStateId.includes("completed") && !finalContainerStateId.includes("noshow") && (oldStatus === AppointmentStatus.NO_SHOW || oldStatus === AppointmentStatus.COMPLETED)) {
                updateAppointmentStatusMutation.mutate({
                    appointmentId: appointmentData.id,
                    appointmentStatus: AppointmentStatus.SCHEDULED
                })
            }

            if (groupChanged || roomChanged || waitingRoomChanged) {
                console.log(`Change detected. Old: {Group: ${oldGroup}, Room: ${oldRoomId}}, New: {Group: ${newEffectiveGroup}, Room: ${newRoomId || 'N/A'}}`);
                addLiveBoardGroupEntryMutation.mutate({
                    appointmentId: appointmentData.id,
                    appointmentLiveBoardGroup: newEffectiveGroup,
                    roomId: newRoomId,
                    waitingRoomId: newWaitingRoomId,
                });
            } else {
                console.log("No change in group or room assignment requiring backend update.");
            }
        }
    }, [data.items, findItemMemoized, addLiveBoardGroupEntryMutation, setIsActuallyDragging, storeId, queryDate]);

    const handleDragCancel = useCallback(() => {
        setActiveId(null);
        setIsActuallyDragging(false);
        console.log("Drag cancelled");
    }, [setIsActuallyDragging]);


    return (
        <BillingDraftDialogContext.Provider value={billingDraftDialogContextValue}>
        <DocDialogContext.Provider value={docDialogContextValue}>
        <AppointmentChatContext.Provider value={appointmentChatContextValue}>
            <div className='flex flex-col gap-2'>
                <div className='flex items-end gap-4 flex-wrap'>
                    <div className='max-w-[300px]'>
                        <Label htmlFor="board-search" className='mb-1'>Suchen</Label>
                        <Input
                            id="board-search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder='Suchen nach Kunden, Behandlern, ...'
                        />
                    </div>
                    <BlutlaborPickupIndicator storeId={storeId} />
                </div>
                <DndContext
                    sensors={sensors}
                    onDragStart={handleDragStart}
                    onDragOver={handleDragOver}
                    onDragEnd={handleDragEnd}
                    onDragCancel={handleDragCancel}
                    collisionDetection={customCollisionDetection}
                >
                    <SortableContext id="root-kanban" items={rootItemIds} strategy={horizontalListSortingStrategy}>
                        <div className={`max-w-[calc(100vw-${sidebarOpen ? '250' : '0'}px)] w-full min-h-[calc(100vh-250px)] flex flex-row overflow-auto items-start gap-4 p-4 rounded-lg bg-slate-100`} style={{ display: 'flex' }}>
                            {getItems(undefined).map((item) => {
                                if (item.container) {
                                    return (
                                        <SortableContainer
                                            key={item.id} id={item.id} title={item.title}
                                            getItems={getItems} childLayout={item.childrenLayout || 'vertical'}
                                            allItems={data.items} activeDraggedItemId={activeId}
                                            findItem={findItemMemoized} getItemDepth={getItemDepthMemoized}
                                            getChildrenOfItem={getChildrenOf}
                                            isCollapsible={item.isCollapsible}
                                            defaultCollapsed={item.defaultCollapsed}
                                            color={item.color}
                                            style={{ flex: '1 1 0', minWidth: 0 }}
                                        />
                                    );
                                }
                                return null;
                            })}
                        </div>
                    </SortableContext>
                    <DragOverlay dropAnimation={null}>{getDragOverlay()}</DragOverlay>
                </DndContext>
            </div>

            <BillingDraftCreatorDialog
                open={billingDraftCreator.open}
                onOpenChange={(open) => {
                    if (!open) {
                        setBillingDraftCreator((prev) => ({ ...prev, open: false }));
                    }
                }}
                preselectedCustomerId={billingDraftCreator.customerId}
                preselectedAppointmentId={billingDraftCreator.appointmentId}
                onSuccess={(billingDraftId) => {
                    const appointmentId = billingDraftCreator.appointmentId;
                    setBillingDraftCreator((prev) => ({ ...prev, open: false }));
                    setBillingDraftDetail({ open: true, billingDraftId, appointmentId });
                    trpcUtils.billingDrafts.getByAppointment.invalidate({ appointmentId });
                }}
            />

            {billingDraftDetail.billingDraftId && (
                <BillingDraftDetailDialog
                    open={billingDraftDetail.open}
                    onOpenChange={(open) => {
                        setBillingDraftDetail((prev) => ({ ...prev, open }));
                        if (!open) {
                            trpcUtils.billingDrafts.getByAppointment.invalidate({ appointmentId: billingDraftDetail.appointmentId });
                        }
                    }}
                    billingDraftId={billingDraftDetail.billingDraftId}
                />
            )}

            {docDialogState !== null && (
                <AddTreatmentDialog
                    customerId={docDialogState.customerId}
                    forceNew={docDialogState.mode === 'new'}
                    preselectedAppointmentId={docDialogState.mode === 'new' ? docDialogState.appointmentId : undefined}
                    treatmentId={docDialogState.mode === 'edit' ? docDialogState.treatmentId : undefined}
                    open={true}
                    onOpenChange={(open) => {
                        if (!open) {
                            setDocDialogState(null);
                        }
                    }}
                    onSuccess={() => {
                        const appointmentId = docDialogState.appointmentId;
                        setDocDialogState(null);
                        trpcUtils.medicalRecord.getLatestTreatmentByAppointment.invalidate({ appointmentId });
                        trpcUtils.medicalRecord.getDocumentationStatusForAppointment.invalidate({ appointmentId });
                    }}
                />
            )}

            <AppointmentChatDialog
                target={chatTarget}
                onClose={() => setChatTarget(null)}
            />
        </AppointmentChatContext.Provider>
        </DocDialogContext.Provider>
        </BillingDraftDialogContext.Provider>
    );
};

const AppointmentsBoard = () => {
    const { selectedStore } = useAppointmentsCalendarStore(useShallow(state => ({ selectedStore: state.selectedStore })));

    return (
        <Content className='!p-4'>
            <div className='flex flex-col p-0'>
                <div className="mb-4"><StoreSelector /></div>
                {selectedStore ? (
                    <KanbanBoard storeId={selectedStore.id} />
                ) : (
                    <div className="flex items-center justify-center h-64">
                        <p className="text-muted-foreground">Please select a store to view the board.</p>
                    </div>
                )}
            </div>
        </Content>
    );
}

export default AppointmentsBoard;